import React, { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import {
  Loader2, RefreshCw, Search, Plus, MoreHorizontal, Star, Building2,
  MessageCircle, Phone, Mail, Filter as FilterIcon, ArrowUpDown,
  CalendarClock, FileText, ArrowRight, ChevronLeft, Activity as ActivityIcon,
  CheckSquare, Sparkles, Stethoscope, Heart, Leaf, Mountain, Sun, Trash2,
  Check, DollarSign, Users, FileSpreadsheet, TrendingUp, Wallet, ChevronRight,
  CheckCircle2, Circle, Zap, LayoutGrid, List as ListIcon, GripVertical,
} from "lucide-react";
import { toast } from "sonner";
import { useGmailConnection } from "@/hooks/useGmailConnection";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Sheet, SheetContent, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

import FacilityAIPanel from "@/components/FacilityAIPanel";
import FacilityProposalsPanel from "@/components/facility/FacilityProposalsPanel";
import {
  FacilityAppointmentsTab, FacilityMessagesTab, FacilityDocumentsTab,
} from "@/components/facility/FacilityDetailTabs";
import {
  AIEmailComposerDialog, GenerateInvoiceDialog, GenerateContractDialog,
} from "@/components/facility/FacilityWorkflowDialogs";
import {
  listFacilityLeads, getFacilityStats, updateFacilityLead, createFacilityLead,
  deleteFacilityLead, archiveFacilityLead, restoreFacilityLead,
  binFacilityLead, unbinFacilityLead, addFacilityNote, sendFacilityEmail,
  advanceFacilityStage, setFacilityStage, logFacilityActivity, clearAdminToken,
  listFacilityEmails, EMAIL_STATUS_META,
  FACILITY_STATUSES, FACILITY_PIPELINE_STAGES, canonicalFacilityStatus,
  FACILITY_EMAIL_ACTIONS, BILLING_FREQUENCIES, PAYMENT_TERMS, PRICING_MODELS,
  FACILITY_CORE_PIPELINE, FACILITY_STAGE_INDEX, STAGE_RECOMMENDED_ACTION,
  disconnectGmail, syncGmailInbox,
  listFacilityContractPatients,
} from "@/lib/api";
import StandingOrdersPanel from "@/components/clients/StandingOrdersPanel";
import ContractPatientProfile from "@/components/clients/ContractPatientProfile";

const STATUS_BADGE = Object.fromEntries(
  FACILITY_STATUSES.map((s) => [s.value, { bg: s.bg, color: s.color, border: s.border, label: s.label }])
);
const labelFor = (s) => STATUS_BADGE[s]?.label || STATUS_BADGE[canonicalFacilityStatus(s)]?.label || s;
const colorFor = (s) => STATUS_BADGE[s] || STATUS_BADGE[canonicalFacilityStatus(s)] || STATUS_BADGE.new_inquiry;
const fmtMoney0 = (v) => v == null ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtDate  = (iso) => { if (!iso) return "—"; try { return format(new Date(iso), "PP"); } catch { return iso; } };
const fmtTime  = (iso) => { if (!iso) return "—"; try { return format(new Date(iso), "p"); } catch { return iso; } };
const fmtDateTime = (iso) => { if (!iso) return "—"; try { return format(new Date(iso), "PPp"); } catch { return iso; } };

// Avatar palette — deterministic by facility id
const AVATARS = [
  { bg: "rgba(20,184,166,0.16)",  fg: "#0F766E", icon: Heart },
  { bg: "rgba(16,185,129,0.16)",  fg: "#065F46", icon: Leaf },
  { bg: "rgba(212,175,55,0.20)",  fg: "#7A5E12", icon: Building2 },
  { bg: "rgba(168,85,247,0.16)",  fg: "#6B21A8", icon: Mountain },
  { bg: "rgba(59,130,246,0.16)",  fg: "#1D4ED8", icon: Stethoscope },
  { bg: "rgba(244,114,182,0.16)", fg: "#9D174D", icon: Sun },
];
const avatarFor = (id) => {
  let h = 0; for (let i = 0; i < (id || "").length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return AVATARS[Math.abs(h) % AVATARS.length];
};

const SORT_OPTIONS = [
  { id: "recent", label: "Sort: Recently Updated" },
  { id: "revenue_desc", label: "Sort: Highest Revenue" },
  { id: "name_asc", label: "Sort: Facility Name" },
  { id: "created_desc", label: "Sort: Newest First" },
];

const PAGE_SIZE = 8;

// =============================================================================
// MAIN PANEL
// =============================================================================
export default function FacilityLeadsPanel({ onSessionExpired }) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [stage, setStage] = useState("all");
  const [sortId, setSortId] = useState("recent");
  const [filterType, setFilterType] = useState("active");   // active | archived | bin
  const [active, setActive] = useState(null);
  // If a facility panel wants to open a contract-patient profile, we lift that
  // to this level so it doesn't get remounted on parent re-renders.
  const [activePatientId, setActivePatientId] = useState(null);
  const [page, setPage] = useState(1);
  const [showAdd, setShowAdd] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // "list" | "kanban"

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [list, st] = await Promise.all([listFacilityLeads(), getFacilityStats().catch(() => null)]);
      setItems(list);
      setStats(st);
    } catch (e) {
      if (e?.response?.status === 401) {
        clearAdminToken(); onSessionExpired?.();
        toast.error("Your session expired. Please sign in again.");
        return;
      }
      toast.error("Failed to load facilities.");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const applyUpdate = (updated) => {
    setItems((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
    if (active?.id === updated.id) setActive(updated);
    getFacilityStats().then(setStats).catch(() => {});
  };

  // Apply scope (active/archived/bin) + stage chip + search + sort
  const visible = useMemo(() => {
    const scoped = items.filter((l) => {
      if (filterType === "bin") return !!l.is_binned;
      if (filterType === "archived") return !!l.is_archived && !l.is_binned;
      return !l.is_archived && !l.is_binned;
    });
    const inStage = stage === "all"
      ? scoped
      : scoped.filter((l) => canonicalFacilityStatus(l.status) === stage);
    const q = query.trim().toLowerCase();
    const searched = q
      ? inStage.filter((l) =>
          [l.facility_name, l.contact_person, l.email, l.phone, l.service_needed, l.account_manager, l.next_step]
            .filter(Boolean).some((v) => String(v).toLowerCase().includes(q)))
      : inStage;
    const sorted = [...searched];
    if (sortId === "revenue_desc") sorted.sort((a, b) => (b.monthly_revenue || 0) - (a.monthly_revenue || 0));
    else if (sortId === "name_asc") sorted.sort((a, b) => (a.facility_name || "").localeCompare(b.facility_name || ""));
    else if (sortId === "created_desc") sorted.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    else sorted.sort((a, b) => new Date(b.last_activity_at || b.created_at || 0) - new Date(a.last_activity_at || a.created_at || 0));
    return sorted;
  }, [items, stage, query, sortId, filterType]);

  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const pageItems = visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  useEffect(() => { if (page > totalPages) setPage(1); }, [page, totalPages]);

  // Stage counts (canonical only — active scope)
  const stageCounts = useMemo(() => {
    const m = { all: 0 };
    FACILITY_PIPELINE_STAGES.forEach((s) => (m[s] = 0));
    items.forEach((l) => {
      if (l.is_archived || l.is_binned) return;
      m.all += 1;
      const c = canonicalFacilityStatus(l.status);
      if (m[c] != null) m[c] += 1;
    });
    return m;
  }, [items]);

  // Activity + Upcoming tasks aggregation across all facilities
  const recent = useMemo(() => {
    const all = [];
    items.forEach((l) => {
      (l.activity_log || []).forEach((e) =>
        all.push({ ...e, facility_name: l.facility_name, facility_id: l.id })
      );
    });
    return all.sort((a, b) => new Date(b.ts || 0) - new Date(a.ts || 0)).slice(0, 5);
  }, [items]);

  const upcoming = useMemo(() => {
    return items
      .filter((l) => l.next_step && (l.next_step_due || l.next_followup_date))
      .map((l) => ({
        id: l.id,
        facility_name: l.facility_name,
        next_step: l.next_step,
        due: l.next_step_due || l.next_followup_date,
      }))
      .sort((a, b) => new Date(a.due || 0) - new Date(b.due || 0))
      .slice(0, 5);
  }, [items]);

  return (
    <div data-testid="facility-leads-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight" style={{ color: "#0B2545" }}>
            Facilities &amp; Providers
          </h1>
          <p className="mt-1 text-sm" style={{ color: "#475569" }}>
            Track business relationships, recurring contracts, and facility lead conversion.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowAdd(true)}
            className="h-10 font-semibold"
            style={{ background: "#0B2545", color: "#FFFFFF" }}
            data-testid="facility-add-button"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Add Facility / Provider
          </Button>
          <Button
            variant="outline" onClick={fetchAll} className="h-10"
            style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
            data-testid="facility-refresh-button"
          >
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* KPI cards — 6 across, clickable filters, matches reference */}
      <div className="mt-6 grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6" data-testid="facility-kpi-cards">
        <KpiCard
          label="TOTAL FACILITIES" value={stats?.total ?? 0} hint="All Facilities" icon={Building2}
          accent="#1D4ED8"
          isActive={filterType === "active" && stage === "all"}
          onClick={() => { setStage("all"); setFilterType("active"); setPage(1); }}
          testId="kpi-total"
        />
        <KpiCard
          label="NEW INQUIRIES" value={stats?.new ?? 0} hint="This Month" icon={FileSpreadsheet}
          accent="#EA580C"
          isActive={filterType === "active" && stage === "new_inquiry"}
          onClick={() => { setStage("new_inquiry"); setFilterType("active"); setPage(1); }}
          testId="kpi-new"
        />
        <KpiCard
          label="QUOTES SENT" value={stats?.quote_sent ?? 0} hint="This Month" icon={FileText}
          accent="#1D4ED8"
          isActive={filterType === "active" && stage === "quote_sent"}
          onClick={() => { setStage("quote_sent"); setFilterType("active"); setPage(1); }}
          testId="kpi-quotes"
        />
        <KpiCard
          label="ACTIVE CLIENTS" value={stats?.active ?? 0} hint="Active" icon={Users}
          accent="#065F46"
          isActive={filterType === "active" && stage === "active_client"}
          onClick={() => { setStage("active_client"); setFilterType("active"); setPage(1); }}
          testId="kpi-active"
        />
        <KpiCard
          label="MONTHLY REVENUE" value={fmtMoney0(stats?.monthly_revenue)} hint="From Facilities" icon={DollarSign}
          accent="#065F46"
          isActive={false}
          onClick={() => { setSortId("revenue_desc"); }}
          testId="kpi-revenue"
        />
        <KpiCard
          label="OUTSTANDING" value={fmtMoney0(stats?.outstanding_receivables)}
          hint={`${stats?.outstanding_invoices || 0} Invoice${stats?.outstanding_invoices === 1 ? "" : "s"}`}
          icon={Wallet} accent="#B91C1C"
          isActive={false}
          onClick={() => { setSortId("recent"); }}
          testId="kpi-outstanding"
        />
      </div>

      {/* Visual 8-stage pipeline stepper */}
      <PipelineStepper
        stageCounts={stageCounts}
        activeStage={stage}
        onSelectStage={(s) => { setStage(s); setFilterType("active"); setPage(1); }}
      />

      {/* Secondary filter chips — Priority / Follow-Up / Lost / Archived / Bin */}
      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="facility-secondary-filters">
        <span className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#94A3B8" }}>
          Filters
        </span>
        <SecondaryChip
          active={stage === "all" && filterType === "active"}
          onClick={() => { setStage("all"); setFilterType("active"); setPage(1); }}
          label="All Active" count={stats?.total ?? 0} testId="filter-all-active" tone="navy"
        />
        <SecondaryChip
          active={stage === "priority_partner"}
          onClick={() => { setStage("priority_partner"); setFilterType("active"); setPage(1); }}
          label="Priority Partner" count={stats?.priority ?? 0} testId="filter-priority"
          accent={{ color: "#6B21A8", bg: "rgba(168,85,247,0.14)", border: "rgba(168,85,247,0.4)" }}
        />
        <SecondaryChip
          active={stage === "needs_followup"}
          onClick={() => { setStage("needs_followup"); setFilterType("active"); setPage(1); }}
          label="Needs Follow-Up" count={stats?.follow_up ?? 0} testId="filter-followup"
          accent={{ color: "#991B1B", bg: "rgba(220,38,38,0.1)", border: "rgba(220,38,38,0.3)" }}
        />
        <SecondaryChip
          active={stage === "lost_deal"}
          onClick={() => { setStage("lost_deal"); setFilterType("active"); setPage(1); }}
          label="Lost Deal" count={stats?.lost ?? 0} testId="filter-lost"
          accent={{ color: "#7F1D1D", bg: "rgba(220,38,38,0.08)", border: "rgba(220,38,38,0.25)" }}
        />
        <SecondaryChip
          active={filterType === "archived"}
          onClick={() => { setFilterType("archived"); setStage("all"); setPage(1); }}
          label="Archived" count={stats?.archived ?? 0} testId="filter-archived"
        />
        <SecondaryChip
          active={filterType === "bin"}
          onClick={() => { setFilterType("bin"); setStage("all"); setPage(1); }}
          label="Bin" count={stats?.binned ?? 0} testId="filter-bin"
        />
      </div>

      {/* Search + Filter + Sort row */}
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px] max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "#94A3B8" }} />
          <Input
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
            placeholder="Search facility, contact, or email…"
            className="h-10 pl-9"
            data-testid="facility-search-input"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="h-10" data-testid="facility-filter-button">
              <FilterIcon className="h-4 w-4 mr-1.5" /> Filter
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={() => setFilterType("active")}>Active facilities</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setFilterType("archived")}>Archived</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setFilterType("bin")}>Bin</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => { setStage("priority_partner"); setFilterType("active"); }}>Priority Partners</DropdownMenuItem>
            <DropdownMenuItem onClick={() => { setStage("needs_followup"); setFilterType("active"); }}>Needs Follow-Up</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="h-10" data-testid="facility-sort-button">
              <ArrowUpDown className="h-4 w-4 mr-1.5" />
              {SORT_OPTIONS.find((o) => o.id === sortId)?.label}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {SORT_OPTIONS.map((o) => (
              <DropdownMenuItem key={o.id} onClick={() => setSortId(o.id)} data-testid={`facility-sort-${o.id}`}>
                {o.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* View mode toggle — List / Kanban */}
        <div
          className="inline-flex rounded-lg border overflow-hidden"
          style={{ borderColor: "#E2E8F0" }}
          data-testid="facility-view-toggle"
        >
          <button
            type="button"
            onClick={() => setViewMode("list")}
            className="inline-flex items-center gap-1.5 px-3 h-10 text-xs font-semibold transition"
            style={{
              background: viewMode === "list" ? "#0B2545" : "#FFFFFF",
              color: viewMode === "list" ? "#FFFFFF" : "#0B2545",
            }}
            data-testid="facility-view-list"
          >
            <ListIcon className="h-3.5 w-3.5" /> List
          </button>
          <button
            type="button"
            onClick={() => setViewMode("kanban")}
            className="inline-flex items-center gap-1.5 px-3 h-10 text-xs font-semibold transition border-l"
            style={{
              borderColor: "#E2E8F0",
              background: viewMode === "kanban" ? "#0B2545" : "#FFFFFF",
              color: viewMode === "kanban" ? "#FFFFFF" : "#0B2545",
            }}
            data-testid="facility-view-kanban"
          >
            <LayoutGrid className="h-3.5 w-3.5" /> Kanban
          </button>
        </div>
      </div>

      {/* Facility list / Kanban */}
      {viewMode === "kanban" ? (
        <FacilityKanbanBoard
          facilities={visible}
          loading={loading}
          activeId={active?.id}
          onOpen={(f) => setActive(f)}
          onStageChanged={applyUpdate}
        />
      ) : (
      <section className="mt-5 rounded-2xl border bg-white overflow-hidden" style={{ borderColor: "#E2E8F0" }}>
        {/* Column headers */}
        <div className="grid grid-cols-12 gap-3 px-5 py-2.5 text-[10px] uppercase tracking-[0.18em] font-semibold border-b"
          style={{ borderColor: "#F1F5F9", color: "#475569", background: "#F8FAFC" }}>
          <div className="col-span-3">Facility / Contact</div>
          <div className="col-span-2">Stage</div>
          <div className="col-span-2">Last Activity</div>
          <div className="col-span-2">Next Action</div>
          <div className="col-span-1">Monthly Rev.</div>
          <div className="col-span-2">Owner</div>
        </div>

        {loading ? (
          <div className="py-20 flex items-center justify-center gap-3" style={{ color: "#475569" }}>
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Loading facilities…</span>
          </div>
        ) : pageItems.length === 0 ? (
          <div className="py-20 text-center" data-testid="facility-empty-state">
            <Building2 className="h-7 w-7 mx-auto mb-3" style={{ color: "#94A3B8" }} />
            <div className="font-display text-lg font-semibold" style={{ color: "#0B2545" }}>
              {query ? "No facilities match your search." : "No facilities yet."}
            </div>
            <p className="mt-1 text-sm" style={{ color: "#475569" }}>
              {query ? "Try a different keyword." : "Add your first facility to start tracking the partnership."}
            </p>
          </div>
        ) : (
          <ul className="divide-y" style={{ borderColor: "#F1F5F9" }}>
            {pageItems.map((l) => (
              <FacilityRow
                key={l.id}
                facility={l}
                isActive={active?.id === l.id}
                onClick={() => setActive(l)}
                onUpdate={applyUpdate}
              />
            ))}
          </ul>
        )}

        {/* Pagination */}
        {visible.length > PAGE_SIZE ? (
          <div className="px-5 py-3 flex items-center justify-between flex-wrap gap-2 border-t" style={{ borderColor: "#F1F5F9" }} data-testid="facility-pagination">
            <span className="text-xs" style={{ color: "#475569" }}>
              Showing {(page - 1) * PAGE_SIZE + 1} to {Math.min(page * PAGE_SIZE, visible.length)} of {visible.length} facilities
            </span>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="h-7 w-7 grid place-items-center rounded-md border text-xs"
                style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
                disabled={page === 1}
              >‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).slice(0, 6).map((n) => (
                <button
                  key={n}
                  onClick={() => setPage(n)}
                  className="h-7 w-7 grid place-items-center rounded-md border text-xs font-semibold"
                  style={{
                    borderColor: page === n ? "#0B2545" : "#E2E8F0",
                    background:  page === n ? "#0B2545" : "#FFFFFF",
                    color:       page === n ? "#FFFFFF" : "#0B2545",
                  }}
                >{n}</button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="h-7 w-7 grid place-items-center rounded-md border text-xs"
                style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
                disabled={page === totalPages}
              >›</button>
            </div>
          </div>
        ) : null}
      </section>
      )}

      {/* Bottom row: Recent Activity + Upcoming Tasks */}
      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        <RecentActivityCard items={recent} />
        <UpcomingTasksCard items={upcoming} onOpen={(id) => setActive(items.find((l) => l.id === id))} />
      </div>

      {/* Right-side detail sheet */}
      <FacilityDetailSheet
        record={active}
        onClose={() => setActive(null)}
        onUpdate={applyUpdate}
        onOpenContractPatient={(pid) => setActivePatientId(pid)}
        onDelete={async (id) => {
          if (!window.confirm("Delete this facility permanently?")) return;
          try {
            await deleteFacilityLead(id);
            setItems((prev) => prev.filter((l) => l.id !== id));
            setActive(null);
            toast.success("Facility deleted.");
          } catch { toast.error("Failed to delete."); }
        }}
        onArchive={async (id, archived) => {
          try {
            const updated = archived ? await archiveFacilityLead(id) : await restoreFacilityLead(id);
            applyUpdate(updated);
            toast.success(archived ? "Archived." : "Restored.");
          } catch { toast.error("Failed."); }
        }}
        onBin={async (id, binned) => {
          try {
            const updated = binned ? await binFacilityLead(id) : await unbinFacilityLead(id);
            applyUpdate(updated);
            toast.success(binned ? "Moved to bin." : "Restored from bin.");
          } catch { toast.error("Failed."); }
        }}
      />

      {/* Add facility dialog */}
      <AddFacilityDialog
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onCreated={(rec) => { setItems((prev) => [rec, ...prev]); setShowAdd(false); setActive(rec); fetchAll(); }}
      />

      {/* Contract-patient profile — opened from the facility's Patients tab */}
      <ContractPatientProfile
        patientId={activePatientId}
        onClose={() => setActivePatientId(null)}
        onUpdate={() => { /* patients tab refreshes via its own effect */ }}
        onDelete={() => setActivePatientId(null)}
      />
    </div>
  );
}

// =============================================================================
// Atoms
// =============================================================================
const KpiCard = ({ label, value, hint, icon: Icon, accent, isActive, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className="text-left rounded-xl bg-white px-4 py-4 transition hover:shadow-sm"
    style={{
      border: isActive ? `2px solid ${accent}` : "1px solid #E2E8F0",
      background: isActive ? `rgba(11,37,69,0.02)` : "#FFF",
    }}
    data-testid={testId}
  >
    <div className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: accent }}>
      {label}
    </div>
    <div
      className="mt-3 grid place-items-center h-11 w-11 rounded-lg"
      style={{ background: `${accent}14`, color: accent }}
    >
      <Icon className="h-5 w-5" />
    </div>
    <div className="mt-3 font-display text-3xl font-bold leading-none tracking-tight" style={{ color: "#0F172A" }}>
      {value}
    </div>
    {hint ? <div className="mt-1.5 text-[11px]" style={{ color: "#94A3B8" }}>{hint}</div> : null}
  </button>
);

const SecondaryChip = ({ active, onClick, label, count, accent, testId, tone }) => {
  const isNavy = tone === "navy";
  const fg = active ? "#FFFFFF" : (accent?.color || "#475569");
  const bg = active ? (isNavy ? "#0B2545" : accent?.color || "#0B2545") : (accent?.bg || "#F8FAFC");
  const bd = active ? (accent?.color || "#0B2545") : (accent?.border || "#E2E8F0");
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-2 h-8 px-3 rounded-full border text-xs font-semibold transition"
      style={{ borderColor: bd, background: bg, color: fg }}
      data-testid={testId}
    >
      <span>{label}</span>
      <span
        className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full text-[10px] font-bold"
        style={{
          background: active ? "rgba(255,255,255,0.18)" : "rgba(11,37,69,0.06)",
          color: active ? "#FFFFFF" : (accent?.color || "#475569"),
        }}
      >{count}</span>
    </button>
  );
};

// =============================================================================
// Visual pipeline stepper: 8 stages from Lead → Active Client
// =============================================================================
const PipelineStepper = ({ stageCounts, activeStage, onSelectStage }) => {
  // Determine "current" stage index — the lowest-numbered stage that still has
  // at least one facility in it (leftmost active stage). Stages before the
  // current are shown ✓, current shows ●, upcoming show ○.
  const activeIdx = FACILITY_CORE_PIPELINE.findIndex((s) => s.value === activeStage);
  const currentIdx = activeIdx >= 0
    ? activeIdx
    : FACILITY_CORE_PIPELINE.findIndex((s) => (stageCounts[s.value] || 0) > 0);
  const highlightIdx = currentIdx >= 0 ? currentIdx : 0;
  const completedCount = FACILITY_CORE_PIPELINE.slice(0, highlightIdx).length;
  const totalStages = FACILITY_CORE_PIPELINE.length;
  const pct = Math.round((completedCount / totalStages) * 100);

  return (
    <div
      className="mt-5 rounded-2xl border bg-white p-5"
      style={{ borderColor: "#E2E8F0" }}
      data-testid="facility-pipeline-stepper"
    >
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold" style={{ color: "#475569" }}>
          Pipeline: From Lead to Active Client
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold" style={{ color: "#475569" }}>
            {completedCount} of {totalStages} stages completed
          </span>
          <div className="w-32 h-1.5 rounded-full overflow-hidden" style={{ background: "#F1F5F9" }}>
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${pct}%`, background: "#10B981" }}
            />
          </div>
          <span className="text-xs font-bold" style={{ color: "#065F46" }}>{pct}%</span>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        {FACILITY_CORE_PIPELINE.map((s, i) => {
          const state = i < highlightIdx ? "done" : i === highlightIdx ? "current" : "upcoming";
          const isSelected = activeStage === s.value;
          const isLast = i === FACILITY_CORE_PIPELINE.length - 1;
          const bg = state === "done"
            ? "#10B981"
            : state === "current" ? "#2563EB" : "#F1F5F9";
          const fg = state === "upcoming" ? "#94A3B8" : "#FFF";
          const boxBg = isSelected ? "rgba(37,99,235,0.06)" : "transparent";
          const boxBorder = isSelected ? "#2563EB" : "transparent";
          return (
            <div key={s.value} className="relative flex items-start">
              <button
                type="button"
                onClick={() => onSelectStage(s.value)}
                className="flex-1 flex flex-col items-center gap-2 rounded-xl p-2 transition"
                style={{ background: boxBg, border: `2px solid ${boxBorder}` }}
                data-testid={`pipeline-stage-${s.value}`}
              >
                <div
                  className="h-9 w-9 rounded-full grid place-items-center shrink-0"
                  style={{ background: bg, color: fg }}
                >
                  {state === "done" ? <Check className="h-4 w-4" strokeWidth={3} />
                    : state === "current" ? <Zap className="h-4 w-4" strokeWidth={2.5} />
                    : <Circle className="h-3.5 w-3.5" />}
                </div>
                <div
                  className="text-[11px] font-semibold text-center leading-tight"
                  style={{ color: state === "upcoming" ? "#94A3B8" : "#0F172A" }}
                >
                  {s.short}
                </div>
                <div
                  className="text-lg font-bold leading-none"
                  style={{ color: state === "current" ? "#2563EB" : state === "done" ? "#065F46" : "#94A3B8" }}
                >
                  {stageCounts[s.value] || 0}
                </div>
              </button>
              {!isLast && (
                <div className="hidden lg:block absolute top-6 -right-1 z-0" style={{ color: "#CBD5E1" }}>
                  <ChevronRight className="h-4 w-4" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// =============================================================================
// Kanban board — 8 stage columns with native HTML5 drag-and-drop between
// columns. On drop, the dragged facility's status is persisted via
// `/api/facility-leads/{id}/set-stage` so the move survives a page refresh.
// Admins may freely move cards forward or backward (b1 behaviour).
// =============================================================================
const FacilityKanbanBoard = ({ facilities, loading, activeId, onOpen, onStageChanged }) => {
  const [dragId, setDragId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [busyId, setBusyId] = useState(null);

  // Group facilities into the 8 canonical pipeline columns using the alias
  // table so legacy status values still land in the right column.
  const grouped = useMemo(() => {
    const map = Object.fromEntries(FACILITY_CORE_PIPELINE.map((s) => [s.value, []]));
    facilities.forEach((f) => {
      const key = canonicalFacilityStatus(f.status);
      if (map[key]) map[key].push(f);
    });
    return map;
  }, [facilities]);

  const onDragStart = (e, facility) => {
    setDragId(facility.id);
    try {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", facility.id);
    } catch { /* some browsers may reject custom types silently */ }
  };

  const onDragEnd = () => {
    setDragId(null);
    setDragOverStage(null);
  };

  const onColumnDragOver = (e, stage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverStage !== stage) setDragOverStage(stage);
  };

  const onColumnDrop = async (e, stage) => {
    e.preventDefault();
    const id = (() => {
      try { return e.dataTransfer.getData("text/plain") || dragId; }
      catch { return dragId; }
    })();
    setDragOverStage(null);
    setDragId(null);
    if (!id) return;
    const facility = facilities.find((f) => f.id === id);
    if (!facility) return;
    if (canonicalFacilityStatus(facility.status) === stage) return;
    // Optimistically update the UI
    const prevStatus = facility.status;
    onStageChanged({ ...facility, status: stage });
    setBusyId(id);
    try {
      const updated = await setFacilityStage(id, stage);
      onStageChanged(updated);
      const label = FACILITY_CORE_PIPELINE.find((s) => s.value === stage)?.label || stage;
      toast.success(`Moved to ${label}.`);
    } catch (err) {
      // Revert on failure
      onStageChanged({ ...facility, status: prevStatus });
      toast.error(err?.response?.data?.detail || "Failed to move facility.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section
      className="mt-5 rounded-2xl border bg-white p-4"
      style={{ borderColor: "#E2E8F0" }}
      data-testid="facility-kanban-board"
    >
      {loading ? (
        <div className="py-20 flex items-center justify-center gap-3" style={{ color: "#475569" }}>
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Loading facilities…</span>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="flex gap-3 min-w-max">
            {FACILITY_CORE_PIPELINE.map((s) => {
              const cards = grouped[s.value] || [];
              const isDropTarget = dragOverStage === s.value && dragId;
              return (
                <div
                  key={s.value}
                  onDragOver={(e) => onColumnDragOver(e, s.value)}
                  onDragLeave={() => dragOverStage === s.value && setDragOverStage(null)}
                  onDrop={(e) => onColumnDrop(e, s.value)}
                  className="w-[260px] shrink-0 rounded-xl border transition"
                  style={{
                    borderColor: isDropTarget ? "#2563EB" : "#E2E8F0",
                    background: isDropTarget ? "rgba(37,99,235,0.06)" : "#F8FAFC",
                  }}
                  data-testid={`kanban-column-${s.value}`}
                >
                  {/* Column header */}
                  <div
                    className="px-3 py-2.5 border-b flex items-center justify-between"
                    style={{ borderColor: "#E2E8F0" }}
                  >
                    <div className="min-w-0">
                      <div
                        className="text-[11px] uppercase tracking-[0.14em] font-bold truncate"
                        style={{ color: "#0B2545" }}
                      >
                        {s.label}
                      </div>
                    </div>
                    <span
                      className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[10px] font-bold"
                      style={{ background: "rgba(11,37,69,0.08)", color: "#0B2545" }}
                      data-testid={`kanban-column-count-${s.value}`}
                    >
                      {cards.length}
                    </span>
                  </div>

                  {/* Cards */}
                  <div className="p-2.5 space-y-2 min-h-[120px]">
                    {cards.length === 0 ? (
                      <div
                        className="rounded-lg border-2 border-dashed py-5 text-center text-[11px]"
                        style={{
                          borderColor: isDropTarget ? "#2563EB" : "#CBD5E1",
                          color: isDropTarget ? "#1D4ED8" : "#94A3B8",
                        }}
                      >
                        {isDropTarget ? "Drop here" : "No facilities"}
                      </div>
                    ) : (
                      cards.map((f) => (
                        <KanbanCard
                          key={f.id}
                          facility={f}
                          isActive={activeId === f.id}
                          isDragging={dragId === f.id}
                          busy={busyId === f.id}
                          onDragStart={(e) => onDragStart(e, f)}
                          onDragEnd={onDragEnd}
                          onClick={() => onOpen(f)}
                        />
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
};

const KanbanCard = ({ facility, isActive, isDragging, busy, onDragStart, onDragEnd, onClick }) => {
  const av = avatarFor(facility.id);
  const Icon = av.icon;
  const owner = facility.account_manager || "Beckett";
  const ownerC = ownerColor(owner);
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      className="rounded-lg border bg-white p-2.5 cursor-grab active:cursor-grabbing hover:shadow-sm transition"
      style={{
        borderColor: isActive ? "#2563EB" : "#E2E8F0",
        opacity: isDragging ? 0.5 : 1,
        boxShadow: isActive ? "0 0 0 1px #2563EB" : undefined,
      }}
      data-testid={`kanban-card-${facility.id}`}
    >
      <div className="flex items-start gap-2">
        <div className="flex items-center gap-1 shrink-0">
          <GripVertical className="h-3 w-3" style={{ color: "#CBD5E1" }} />
          <div
            className="h-7 w-7 grid place-items-center rounded-md"
            style={{ background: av.bg, color: av.fg }}
          >
            <Icon className="h-3.5 w-3.5" />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div
            className="text-[12px] font-semibold truncate flex items-center gap-1"
            style={{ color: "#0B2545" }}
          >
            {facility.facility_name}
            {facility.is_priority ? (
              <Star className="h-2.5 w-2.5 fill-current" style={{ color: "#D4AF37" }} />
            ) : null}
          </div>
          <div className="text-[10px] truncate" style={{ color: "#64748B" }}>
            {facility.contact_person || "—"}
          </div>
        </div>
        {busy ? (
          <Loader2 className="h-3 w-3 animate-spin shrink-0" style={{ color: "#2563EB" }} />
        ) : null}
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <div
          className="h-5 w-5 rounded-full grid place-items-center text-[9px] font-bold"
          style={{ background: ownerC.bg, color: ownerC.fg }}
          title={owner}
        >
          {ownerInitial(owner)}
        </div>
        <div className="text-[10px] font-semibold" style={{ color: "#0B2545" }}>
          {facility.monthly_revenue != null ? fmtMoney0(facility.monthly_revenue) : ""}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// Facility list row — matches reference layout
// =============================================================================
const isOverdue = (iso) => {
  if (!iso) return false;
  try {
    const d = new Date(iso);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today;
  } catch { return false; }
};

const ownerInitial = (name) => (name || "?").trim().charAt(0).toUpperCase();

const OWNER_COLORS = [
  { bg: "#0B2545", fg: "#FFF" }, // Beckett - navy
  { bg: "#065F46", fg: "#FFF" }, // Kierra - green
  { bg: "#7A5E12", fg: "#FFF" }, // Amanda - gold
  { bg: "#6B21A8", fg: "#FFF" }, // Tyler - purple
];
const ownerColor = (name) => {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return OWNER_COLORS[Math.abs(h) % OWNER_COLORS.length];
};

const FacilityRow = ({ facility, isActive, onClick, onUpdate }) => {
  const av = avatarFor(facility.id);
  const c = colorFor(facility.status);
  const Icon = av.icon;
  const lastActIso = facility.last_activity_at || facility.last_contact_date || facility.created_at;
  const nextDue = facility.next_step_due || facility.next_followup_date;
  const nextOverdue = isOverdue(nextDue);
  const owner = facility.account_manager || "Beckett";
  const ownerC = ownerColor(owner);
  const isActiveClient = facility.status === "active_client" || facility.status === "contract_active";

  return (
    <li
      onClick={onClick}
      className="grid grid-cols-12 gap-3 px-5 py-3.5 hover:bg-[#F8FAFC] cursor-pointer transition relative"
      style={{
        background: isActive ? "rgba(37,99,235,0.05)" : "transparent",
        ...(isActive ? { boxShadow: "inset 4px 0 0 #2563EB" } : {}),
      }}
      data-testid={`facility-row-${facility.id}`}
    >
      {/* Facility / Contact */}
      <div className="col-span-3 flex items-center gap-3 min-w-0">
        <div className="h-10 w-10 grid place-items-center rounded-xl shrink-0"
          style={{ background: av.bg, color: av.fg }}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate flex items-center gap-1.5" style={{ color: "#0B2545" }}>
            {facility.facility_name}
            {facility.is_priority ? <Star className="h-3 w-3 fill-current" style={{ color: "#D4AF37" }} /> : null}
          </div>
          <div className="text-xs truncate" style={{ color: "#475569" }}>
            {facility.contact_person || "—"}
          </div>
          <div className="text-[11px] truncate" style={{ color: "#94A3B8" }}>
            {facility.title_role || "—"}
          </div>
        </div>
      </div>

      {/* Stage */}
      <div className="col-span-2 flex items-center">
        <span
          className="inline-flex items-center rounded-md border px-2 py-1 text-[11px] font-semibold"
          style={{ background: c.bg, color: c.color, borderColor: c.border }}
          data-testid={`row-stage-${facility.id}`}
        >
          {c.label}
        </span>
      </div>

      {/* Last Activity */}
      <div className="col-span-2 flex flex-col justify-center">
        <div className="flex items-center gap-1.5 text-xs" style={{ color: "#0B2545" }}>
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ background: facility.last_activity_at ? "#10B981" : "#94A3B8" }}
          />
          <span className="truncate">
            {(() => {
              const log = facility.activity_log || [];
              if (log.length === 0) return "Created";
              const last = log[log.length - 1] || {};
              return last.summary || last.action || "Updated";
            })().replace(/_/g, " ").slice(0, 24)}
          </span>
        </div>
        <div className="text-[11px] mt-0.5" style={{ color: "#94A3B8" }}>
          {fmtDateTime(lastActIso)}
        </div>
      </div>

      {/* Next Action */}
      <div className="col-span-2 flex flex-col justify-center min-w-0">
        <div className="text-xs truncate" style={{ color: "#0B2545" }}>
          {facility.next_step || (STAGE_RECOMMENDED_ACTION[canonicalFacilityStatus(facility.status)] || "—").split(/[.—]/)[0].slice(0, 28)}
        </div>
        <div
          className="text-[11px] mt-0.5 font-semibold"
          style={{ color: nextOverdue ? "#B91C1C" : "#94A3B8" }}
        >
          {nextDue ? `Due: ${fmtDate(nextDue)}` : "—"}
        </div>
      </div>

      {/* Monthly Rev. */}
      <div className="col-span-1 flex flex-col justify-center">
        <div className="text-sm font-semibold" style={{ color: "#0B2545" }}>
          {facility.monthly_revenue != null ? fmtMoney0(facility.monthly_revenue) : "—"}
        </div>
        <div className="text-[11px]" style={{ color: "#94A3B8" }}>
          {facility.monthly_revenue != null ? (isActiveClient ? "Current" : "Est.") : ""}
        </div>
      </div>

      {/* Owner */}
      <div className="col-span-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="h-7 w-7 rounded-full grid place-items-center text-[11px] font-bold shrink-0"
            style={{ background: ownerC.bg, color: ownerC.fg }}
          >
            {ownerInitial(owner)}
          </div>
          <span className="text-xs truncate" style={{ color: "#0B2545" }}>{owner}</span>
        </div>
        <RowMenu facility={facility} onUpdate={onUpdate} onClick={(e) => e.stopPropagation()} />
      </div>
    </li>
  );
};

const RowMenu = ({ facility, onUpdate, onClick }) => {
  const advance = async () => {
    try {
      const updated = await advanceFacilityStage(facility.id);
      onUpdate(updated);
      toast.success(`Moved to ${labelFor(updated.status)}.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Cannot advance."); }
  };
  return (
    <div onClick={onClick}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="h-7 w-7 grid place-items-center rounded-md hover:bg-[#F1F5F9]"
            data-testid={`facility-row-menu-${facility.id}`}
          >
            <MoreHorizontal className="h-4 w-4" style={{ color: "#475569" }} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuItem onClick={advance}>
            <ArrowRight className="h-3.5 w-3.5 mr-2" /> Move to next stage
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => window.open(`mailto:${facility.email || ""}`)}>
            <Mail className="h-3.5 w-3.5 mr-2" /> Send email
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => window.open(`tel:${facility.phone || ""}`)}>
            <Phone className="h-3.5 w-3.5 mr-2" /> Call
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

// =============================================================================
// Detail sheet (slide-in right)
// =============================================================================
export const FacilityDetailSheet = ({ record, onClose, onUpdate, onDelete, onArchive, onBin, onOpenContractPatient }) => {
  const [tab, setTab] = useState("overview");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [savingField, setSavingField] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  // Billing Summary edit flow — separate from the Facility Information
  // card's `editing` state so operators can edit billing independently
  // of the pipeline stage.
  const [editingBilling, setEditingBilling] = useState(false);
  const [billingForm, setBillingForm] = useState(null);
  const [savingBilling, setSavingBilling] = useState(false);
  const [note, setNote] = useState("");
  const [showEmail, setShowEmail] = useState(false);
  const [showAiEmail, setShowAiEmail] = useState(false);
  const [showGenInvoice, setShowGenInvoice] = useState(false);
  const [showGenContract, setShowGenContract] = useState(false);
  const [emailAction, setEmailAction] = useState("intro_followup");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailAmount, setEmailAmount] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [followUpDate, setFollowUpDate] = useState("");
  const [followUpStep, setFollowUpStep] = useState("");
  // Email thread (loaded when Activity tab opens)
  const [emailThread, setEmailThread] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);

  // Gmail connection status (per-admin) — powers routing + the Connect strip
  const { gmail, connecting: gmailConnecting, connect: connectGmail, refresh: refreshGmail } =
    useGmailConnection(record?.id);
  const [gmailBusy, setGmailBusy] = useState(false);

  const disconnectGmailAcct = async () => {
    if (!window.confirm("Disconnect Gmail? Facility emails will fall back to Resend.")) return;
    try {
      await disconnectGmail();
      toast.success("Gmail disconnected.");
      refreshGmail();
    } catch (e) { toast.error(e?.response?.data?.detail || "Disconnect failed."); }
  };

  const syncGmail = async () => {
    try {
      setGmailBusy(true);
      const res = await syncGmailInbox();
      toast.success(`Synced · ${res.routed_to_facilities} to facilities · ${res.routed_to_leads} to leads`);
      // Refresh the local thread so newly-attached inbound replies appear
      if (record?.id) {
        try { setEmailThread(await listFacilityEmails(record.id)); } catch { /* noop */ }
      }
      refreshGmail();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gmail sync failed."); }
    finally { setGmailBusy(false); }
  };

  useEffect(() => {
    if (record) {
      setForm({
        facility_name: record.facility_name || "",
        contact_person: record.contact_person || "",
        title_role: record.title_role || "",
        email: record.email || "",
        phone: record.phone || "",
        address: record.address || "",
        account_manager: record.account_manager || "",
        facility_type: record.facility_type || "",
        monthly_revenue: record.monthly_revenue ?? "",
        contract_value: record.contract_value ?? "",
        monthly_volume_estimate: record.monthly_volume_estimate ?? "",
        contract_start_date: record.contract_start_date || "",
        contract_end_date: record.contract_end_date || "",
        contract_renewal_date: record.contract_renewal_date || "",
        billing_frequency: record.billing_frequency || "",
        payment_terms: record.payment_terms || "",
        pricing_model: record.pricing_model || "",
        accounts_payable_contact: record.accounts_payable_contact || "",
        billing_email: record.billing_email || "",
        next_step: record.next_step || "",
        next_step_due: record.next_step_due || "",
        notes: record.notes || "",
        tags: (record.tags || []).join(", "),
      });
      setEditing(false); setShowEmail(false); setShowActivityModal(false); setShowFollowUp(false);
      setNote(""); setTab("overview"); setEmailError("");
      setEmailThread([]);
      setEditingBilling(false);
      setBillingForm({
        pricing_model: record.pricing_model || "",
        payment_terms: record.payment_terms || "",
        billing_frequency: record.billing_frequency || "",
        contract_value: record.contract_value ?? "",
        outstanding_balance: record.outstanding_balance ?? "",
        last_payment_date: record.last_payment_date || "",
        next_invoice_date: record.next_invoice_date || "",
      });
    }
  }, [record?.id]);

  // Load email thread whenever the Activity tab is opened (or record changes)
  useEffect(() => {
    if (!record?.id || tab !== "activity") return;
    let cancelled = false;
    (async () => {
      try {
        setThreadLoading(true);
        const items = await listFacilityEmails(record.id);
        if (!cancelled) setEmailThread(items);
      } catch {
        if (!cancelled) setEmailThread([]);
      } finally {
        if (!cancelled) setThreadLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [record?.id, tab]);

  if (!record) return null;
  const av = avatarFor(record.id);
  const c = colorFor(record.status);
  const Icon = av.icon;

  const save = async () => {
    if (!form) return;
    try {
      setSavingField(true);
      const body = {
        facility_name: form.facility_name || undefined,
        contact_person: form.contact_person || undefined,
        title_role: form.title_role || null,
        email: form.email || undefined,
        phone: form.phone || undefined,
        address: form.address || null,
        account_manager: form.account_manager || null,
        facility_type: form.facility_type || null,
        monthly_revenue: form.monthly_revenue === "" ? null : Number(form.monthly_revenue),
        contract_value: form.contract_value === "" ? null : Number(form.contract_value),
        monthly_volume_estimate: form.monthly_volume_estimate === "" ? null : Number(form.monthly_volume_estimate),
        contract_start_date: form.contract_start_date || null,
        contract_end_date: form.contract_end_date || null,
        contract_renewal_date: form.contract_renewal_date || null,
        billing_frequency: form.billing_frequency || null,
        payment_terms: form.payment_terms || null,
        pricing_model: form.pricing_model || null,
        accounts_payable_contact: form.accounts_payable_contact || null,
        billing_email: form.billing_email || null,
        next_step: form.next_step || null,
        next_step_due: form.next_step_due || null,
        notes: form.notes || null,
        tags: form.tags ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : null,
      };
      const updated = await updateFacilityLead(record.id, body);
      onUpdate(updated);
      setEditing(false);
      toast.success("Facility updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update.");
    } finally { setSavingField(false); }
  };

  const saveBilling = async () => {
    if (!billingForm) return;
    try {
      setSavingBilling(true);
      const body = {
        pricing_model: billingForm.pricing_model || null,
        payment_terms: billingForm.payment_terms || null,
        billing_frequency: billingForm.billing_frequency || null,
        contract_value: billingForm.contract_value === "" ? null : Number(billingForm.contract_value),
        outstanding_balance: billingForm.outstanding_balance === "" ? null : Number(billingForm.outstanding_balance),
        last_payment_date: billingForm.last_payment_date || null,
        next_invoice_date: billingForm.next_invoice_date || null,
      };
      const updated = await updateFacilityLead(record.id, body);
      onUpdate(updated);
      setEditingBilling(false);
      toast.success("Billing updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update billing.");
    } finally { setSavingBilling(false); }
  };

  const cancelBillingEdit = () => {
    setBillingForm({
      pricing_model: record.pricing_model || "",
      payment_terms: record.payment_terms || "",
      billing_frequency: record.billing_frequency || "",
      contract_value: record.contract_value ?? "",
      outstanding_balance: record.outstanding_balance ?? "",
      last_payment_date: record.last_payment_date || "",
      next_invoice_date: record.next_invoice_date || "",
    });
    setEditingBilling(false);
  };

  const advance = async () => {
    try {
      const updated = await advanceFacilityStage(record.id);
      onUpdate(updated);
      toast.success(`Moved to ${labelFor(updated.status)}.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Cannot advance."); }
  };

  const sendActivity = async (action, summary) => {
    try {
      const res = await logFacilityActivity(record.id, { action, summary });
      onUpdate({ ...record, activity_log: res.activity_log, last_activity_at: res.entry.ts });
      toast.success("Activity logged.");
      setShowActivityModal(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to log."); }
  };

  const scheduleFollowUp = async () => {
    if (!followUpDate || !followUpStep.trim()) {
      toast.error("Add the follow-up description and date.");
      return;
    }
    try {
      const updated = await updateFacilityLead(record.id, {
        next_step: followUpStep.trim(),
        next_step_due: followUpDate,
        next_followup_date: followUpDate,
      });
      onUpdate(updated);
      await logFacilityActivity(record.id, {
        action: "follow_up_scheduled",
        summary: `Scheduled follow-up: ${followUpStep.trim()} (due ${followUpDate})`,
      });
      toast.success("Follow-up scheduled.");
      setShowFollowUp(false); setFollowUpDate(""); setFollowUpStep("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };

  const createQuote = () => {
    setEmailAction("send_quote"); setShowEmail(true);
  };

  const sendEmail = async () => {
    try {
      setEmailSending(true);
      setEmailError("");
      const body = {
        action: emailAction,
        custom_message: emailMessage || undefined,
        amount: emailAmount === "" ? undefined : Number(emailAmount),
      };
      const res = await sendFacilityEmail(record.id, body);
      const updated = res?.lead ? res.lead : res;
      onUpdate(updated);
      // Refresh the thread if it's already loaded
      if (tab === "activity") {
        try { setEmailThread(await listFacilityEmails(record.id)); } catch { /* noop */ }
      }
      setShowEmail(false); setEmailMessage(""); setEmailAmount(""); setEmailError("");
      const provider = res?.provider || "resend";
      if (provider === "gmail") {
        toast.success(`Sent from Gmail (${res?.from || "your Gmail"}) — reply will auto-attach here.`);
      } else {
        toast.success("Email sent — delivery status will update as Resend reports events.");
      }
    } catch (e) {
      // Surface the exact backend detail so operators know why delivery failed
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      const msg = detail
        ? `Failed (${status || "error"}): ${detail}`
        : `Failed: ${e?.message || "Network error contacting the server."}`;
      setEmailError(msg);
      toast.error(msg);
    } finally { setEmailSending(false); }
  };

  return (
    <Dialog open={!!record} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-6xl w-[calc(100vw-2rem)] max-h-[92vh] p-0 overflow-hidden flex flex-col"
        data-testid="facility-detail-panel"
      >
        <DialogTitle className="sr-only">Facility detail — {record.facility_name}</DialogTitle>
        <DialogDescription className="sr-only">
          Centered detail modal with Overview, Contacts, Request Details, Services,
          Quote &amp; Pricing, Contract, Documents, and Notes &amp; Activity tabs.
          Kanban remains visible underneath (dimmed).
        </DialogDescription>
        {/* Header */}
        <div className="px-5 py-4 border-b" style={{ borderColor: "#F1F5F9" }}>
          <div className="flex items-start justify-between gap-2">
            <button onClick={onClose} className="text-[11px] inline-flex items-center gap-1" style={{ color: "#64748B" }} data-testid="facility-detail-back">
              <ChevronLeft className="h-3.5 w-3.5" /> Back
            </button>
          </div>
          <div className="mt-2 flex items-start gap-3">
            <div className="h-12 w-12 grid place-items-center rounded-xl shrink-0"
              style={{ background: av.bg, color: av.fg }}>
              <Icon className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <div className="font-display text-xl font-semibold truncate" style={{ color: "#0B2545" }} data-testid="facility-detail-title">
                {record.facility_name}
              </div>
              <div className="mt-1">
                <span
                  className="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                  style={{ background: c.bg, color: c.color, borderColor: c.border }}
                  data-testid="facility-status-pill"
                >{c.label}</span>
              </div>
              <div className="mt-2 text-[11px]" style={{ color: "#64748B" }}>
                Lead ID: <span className="font-mono">{record.id.slice(0, 8).toUpperCase()}</span>
              </div>
              <div className="text-[11px]" style={{ color: "#94A3B8" }}>
                Added: {fmtDate(record.created_at)}
              </div>
            </div>
          </div>

          {/* Quick action tiles */}
          <div className="mt-4 grid grid-cols-4 gap-2" data-testid="facility-quick-action-tiles">
            <QuickActionTile icon={MessageCircle} label="Message" onClick={() => { setEmailAction("intro_followup"); setShowEmail(true); }} testId="qa-message" />
            <QuickActionTile icon={Phone} label="Call" onClick={() => window.open(`tel:${record.phone || ""}`)} testId="qa-call" />
            <QuickActionTile icon={Mail} label="Email" onClick={() => { setShowEmail(true); }} testId="qa-email" />
            <QuickActionTile icon={MoreHorizontal} label="More" menu={(
              <>
                <DropdownMenuItem onClick={() => onArchive(record.id, !record.is_archived)} data-testid="more-archive">
                  {record.is_archived ? "Restore from Archive" : "Archive"}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onBin(record.id, !record.is_binned)} data-testid="more-bin">
                  {record.is_binned ? "Restore from Bin" : "Move to Bin"}
                </DropdownMenuItem>
                {record.is_binned ? (
                  <DropdownMenuItem onClick={() => onDelete(record.id)} style={{ color: "#B91C1C" }} data-testid="more-delete">
                    <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete permanently
                  </DropdownMenuItem>
                ) : null}
              </>
            )} testId="qa-more" />
          </div>

          {/* Gmail connect / sync strip — mirrors the Lead Communications panel */}
          <div
            className="mt-3 rounded-lg border p-2.5 flex items-center justify-between gap-2 flex-wrap"
            style={{
              borderColor: gmail.connected ? "rgba(16,185,129,0.35)" : "#E2E8F0",
              background: gmail.connected ? "rgba(16,185,129,0.06)" : "#F8FAFC",
            }}
            data-testid="facility-gmail-strip"
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-7 w-7 rounded-md grid place-items-center shrink-0 relative"
                style={{ background: gmail.connected ? "rgba(16,185,129,0.15)" : "rgba(148,163,184,0.15)",
                         color: gmail.connected ? "#065F46" : "#64748B" }}>
                <Mail className="h-3.5 w-3.5" />
                {gmail.connected ? (
                  <span
                    className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2"
                    style={{ background: "#10B981", borderColor: "#FFFFFF" }}
                    aria-hidden="true"
                  />
                ) : null}
              </div>
              <div className="min-w-0">
                <div className="text-[12px] font-bold flex items-center gap-1.5" style={{ color: gmail.connected ? "#065F46" : "#0B2545" }}>
                  {gmail.connected ? (
                    <>
                      <span
                        className="inline-block h-2 w-2 rounded-full"
                        style={{ background: "#10B981" }}
                        aria-hidden="true"
                      />
                      <span data-testid="facility-gmail-status-label">Gmail Connected</span>
                    </>
                  ) : (
                    <span data-testid="facility-gmail-status-label">Gmail not connected</span>
                  )}
                  {gmail.connected ? (
                    <span
                      className="inline-flex items-center rounded-full px-1.5 py-0 text-[9px] font-bold uppercase tracking-wider"
                      style={{ background: "rgba(16,185,129,0.18)", color: "#065F46" }}
                      data-testid="facility-gmail-badge-connected"
                    >Active</span>
                  ) : (
                    <span
                      className="inline-flex items-center rounded-full px-1.5 py-0 text-[9px] font-bold uppercase tracking-wider"
                      style={{ background: "rgba(148,163,184,0.2)", color: "#475569" }}
                      data-testid="facility-gmail-badge-disconnected"
                    >Resend fallback</span>
                  )}
                </div>
                <div className="text-[10px] truncate" style={{ color: "#475569" }}>
                  {gmail.connected ? (
                    <>
                      Connected account:{" "}
                      <b data-testid="facility-gmail-email">{gmail.email || "—"}</b>
                      {gmail.last_sync_at ? (
                        <> · Last sync {fmtDateTime(gmail.last_sync_at)}</>
                      ) : (
                        <> · Auto-syncs every 2 min</>
                      )}
                      {record.last_email_at ? (
                        <> · Last sent {fmtDateTime(record.last_email_at)}</>
                      ) : null}
                    </>
                  ) : (
                    "Connect Gmail to send from your Workspace address · replies auto-attach to this facility."
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {gmail.connected ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      className="h-7 text-[10px] px-2.5 font-bold"
                      style={{ background: "#10B981", color: "#FFFFFF" }}
                      data-testid="facility-gmail-connected-trigger"
                    >
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Connected
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    <DropdownMenuItem
                      onClick={() => window.open("https://mail.google.com", "_blank", "noopener,noreferrer")}
                      data-testid="facility-gmail-open"
                    >
                      <Mail className="h-3.5 w-3.5 mr-2" style={{ color: "#D4AF37" }} /> Open Gmail
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={syncGmail}
                      disabled={gmailBusy}
                      data-testid="facility-gmail-sync"
                    >
                      {gmailBusy ? (
                        <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="h-3.5 w-3.5 mr-2" style={{ color: "#D4AF37" }} />
                      )}
                      Sync inbox
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={connectGmail} data-testid="facility-gmail-reconnect">
                      <RefreshCw className="h-3.5 w-3.5 mr-2" style={{ color: "#2563EB" }} /> Reconnect Gmail
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={disconnectGmailAcct}
                      style={{ color: "#B91C1C" }}
                      data-testid="facility-gmail-disconnect"
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-2" /> Disconnect Gmail
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button size="sm" onClick={connectGmail} disabled={gmailConnecting}
                  className="h-7 text-[10px] px-2" style={{ background: "#0B2545", color: "#FFF" }}
                  data-testid="facility-gmail-connect">
                  {gmailConnecting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Mail className="h-3 w-3 mr-1" />}
                  {gmailConnecting ? "Connecting…" : "Connect Gmail"}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col overflow-hidden">
          <div
            className="w-full border-b flex items-center justify-between pr-2 shrink-0"
            style={{ borderColor: "#F1F5F9" }}
          >
            <TabsList
              className="rounded-none border-0 bg-transparent px-3 h-10"
              data-testid="facility-detail-tabs"
            >
              {[
                { id: "overview",         label: "Overview" },
                { id: "contacts",         label: "Contacts" },
                { id: "request-details",  label: "Request Details", icon: FileText },
                { id: "services",         label: "Services" },
                { id: "quote-pricing",    label: "Quote & Pricing" },
                { id: "contract",         label: "Contract" },
                { id: "documents",        label: "Documents", icon: FileText },
                { id: "notes-activity",   label: "Notes & Activity" },
              ].map((t) => (
                <TabsTrigger
                  key={t.id} value={t.id}
                  className="text-[12px] data-[state=active]:bg-transparent data-[state=active]:font-semibold rounded-none border-b-2 border-transparent data-[state=active]:border-[#0B2545] px-3 h-10"
                  style={{ color: tab === t.id ? "#0B2545" : "#64748B" }}
                  data-testid={`facility-tab-${t.id}`}
                >
                  {t.icon ? <t.icon className="h-3 w-3 mr-1" style={{ color: "#D4AF37" }} /> : null}
                  {t.label}
                </TabsTrigger>
              ))}
              {/* Hidden triggers so Radix accepts programmatic activation of
                  secondary tabs opened from the More dropdown. */}
              {["patients", "orders", "appointments", "messages", "ai", "proposals"].map((hidden) => (
                <TabsTrigger
                  key={hidden}
                  value={hidden}
                  className="hidden"
                  tabIndex={-1}
                  aria-hidden="true"
                  data-testid={`facility-tab-${hidden}`}
                />
              ))}
            </TabsList>

            {/* More ("...") dropdown — exposes the advanced secondary tabs
                (Patients, Standing Orders, Appointments, Messages, AI,
                Proposals, Billing, Activity). The hidden TabsTrigger values
                above (and the existing primary triggers for quote-pricing /
                notes-activity) keep each secondary tab mounted so content
                renders correctly when picked here. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="h-7 w-7 grid place-items-center rounded-md hover:bg-[#F1F5F9]"
                  data-testid="facility-detail-more-trigger"
                  title="More tabs"
                >
                  <MoreHorizontal className="h-4 w-4" style={{ color: "#475569" }} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {[
                  { id: "patients",       label: "Patients",         icon: Users },
                  { id: "orders",         label: "Standing Orders",  icon: FileSpreadsheet },
                  { id: "appointments",   label: "Appointments",     icon: CalendarClock },
                  { id: "messages",       label: "Messages",         icon: MessageCircle },
                  { id: "ai",             label: "AI",               icon: Sparkles },
                  { id: "proposals",      label: "Proposals",        icon: FileText },
                  { id: "quote-pricing",  label: "Billing",          icon: DollarSign },
                  { id: "notes-activity", label: "Activity",         icon: ActivityIcon },
                ].map((item) => (
                  <DropdownMenuItem
                    key={item.id}
                    onClick={() => setTab(item.id)}
                    data-testid={`facility-more-item-${item.id}`}
                  >
                    <item.icon className="h-3.5 w-3.5 mr-2" style={{ color: "#D4AF37" }} />
                    {item.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex-1 overflow-y-auto" style={{ background: "#F8FAFC" }}>
            {/* OVERVIEW — matches reference layout */}
            <TabsContent value="overview" className="m-0 p-4 space-y-4" data-testid="facility-tab-content-overview">

              {/* FACILITY INFORMATION card */}
              <InfoCard
                title="Facility Information"
                testId="facility-info-card"
                onEdit={() => setEditing(true)}
                editing={editing}
                onSave={save}
                onCancel={() => setEditing(false)}
                saving={savingField}
              >
                {editing ? (
                  <EditFields form={form} setForm={setForm} />
                ) : (
                  <>
                    <PairRow label="Type" value={record.facility_type || "—"} />
                    <PairRow label="Primary Contact" value={record.contact_person || "—"} />
                    <PairRow label="Phone" value={record.phone || "—"} />
                    <PairRow label="Email" value={record.email || "—"} link={record.email ? `mailto:${record.email}` : null} valueColor="#2563EB" />
                    <PairRow label="Address" value={record.address || "—"} />
                    <PairRow label="Account Manager" value={record.account_manager || "Unassigned"} />
                    {(record.services_of_interest?.length || record.service_needed) ? (
                      <div className="pt-3 mt-1 border-t" style={{ borderColor: "#F1F5F9" }}>
                        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-2" style={{ color: "#475569" }}>
                          Services of Interest
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {(record.services_of_interest?.length ? record.services_of_interest : [record.service_needed].filter(Boolean))
                            .map((s) => (
                              <span key={s} className="rounded-md border px-2 py-0.5 text-[11px] font-medium"
                                style={{ borderColor: "rgba(37,99,235,0.3)", background: "rgba(37,99,235,0.06)", color: "#1D4ED8" }}>
                                {s}
                              </span>
                            ))}
                        </div>
                      </div>
                    ) : null}
                  </>
                )}
              </InfoCard>

              {/* BILLING SUMMARY card — editable, decoupled from pipeline stage */}
              <InfoCard
                title="Billing Summary"
                testId="billing-summary-card"
                onEdit={() => setEditingBilling(true)}
                editing={editingBilling}
                onSave={saveBilling}
                onCancel={cancelBillingEdit}
                saving={savingBilling}
              >
                {editingBilling ? (
                  <BillingEditFields form={billingForm} setForm={setBillingForm} />
                ) : (
                  <>
                    <PairRow label="Pricing Model" value={PRICING_MODELS.find((m) => m.value === record.pricing_model)?.label || record.pricing_model || "—"} />
                    <PairRow label="Payment Terms" value={PAYMENT_TERMS.find((m) => m.value === record.payment_terms)?.label || record.payment_terms || "—"} />
                    <PairRow label="Billing Frequency" value={BILLING_FREQUENCIES.find((m) => m.value === record.billing_frequency)?.label || record.billing_frequency || "—"} />
                    <PairRow label="Contract Value" value={record.contract_value != null ? `${fmtMoney0(record.contract_value)} / month` : "—"} />
                    <PairRow label="Outstanding Balance"
                      value={record.outstanding_balance != null ? fmtMoney0(record.outstanding_balance) : "$0.00"}
                      valueColor={record.outstanding_balance > 0 ? "#B91C1C" : "#065F46"}
                    />
                    <PairRow label="Last Payment" value={fmtDate(record.last_payment_date)} />
                    <PairRow label="Next Invoice" value={fmtDate(record.next_invoice_date)} />
                  </>
                )}
              </InfoCard>

              {/* NEXT ACTION card */}
              <NextActionCard
                record={record}
                onSendEmail={() => setShowAiEmail(true)}
                onScheduleFollowUp={() => setShowFollowUp(true)}
                onAdvance={advance}
                onGenerateProposal={() => setTab("proposals")}
                onGenerateInvoice={() => setShowGenInvoice(true)}
                onGenerateContract={() => setShowGenContract(true)}
                onCall={() => window.open(`tel:${record.phone || ""}`)}
              />

              {/* AI ASSISTANT preview + Ask AI button */}
              <AssistantPreviewCard
                record={record}
                onOpenFullAI={() => setTab("ai")}
                onFacilityUpdate={onUpdate}
              />

              {/* RECENT ACTIVITY (mini) */}
              <RecentActivityMini record={record} onViewAll={() => setTab("activity")} />
            </TabsContent>

            {/* CONTACTS */}
            <TabsContent value="contacts" className="m-0 p-5 space-y-3" data-testid="facility-tab-content-contacts">
              <DetailSection title="Primary Contact">
                <div className="grid gap-2.5 text-sm">
                  <Pair label="Name" value={record.contact_person} />
                  <Pair label="Role" value={record.title_role} />
                  <Pair label="Phone" value={record.phone} link={record.phone ? `tel:${record.phone}` : null} />
                  <Pair label="Email" value={record.email} link={record.email ? `mailto:${record.email}` : null} />
                </div>
              </DetailSection>
              <DetailSection title="Billing Contact">
                <div className="grid gap-2.5 text-sm">
                  <Pair label="Accounts Payable" value={record.accounts_payable_contact} />
                  <Pair label="Billing Email" value={record.billing_email} link={record.billing_email ? `mailto:${record.billing_email}` : null} />
                </div>
              </DetailSection>
            </TabsContent>

            {/* REQUEST DETAILS — surfaces the intake fields the facility submitted */}
            <TabsContent value="request-details" className="m-0 p-5 space-y-3" data-testid="facility-tab-content-request-details">
              <DetailSection title="Request">
                <div className="grid gap-2.5 text-sm">
                  <Pair label="Request ID" value={record.request_id ? `FCL-${record.request_id}` : "—"} />
                  <Pair label="Received" value={record.created_at ? new Date(record.created_at).toLocaleString() : "—"} />
                  <Pair label="Source" value={record.lead_source || record.source || "Web form"} />
                  <Pair label="Patient Volume" value={record.patient_volume} />
                  <Pair label="Start Date" value={record.service_start_date || record.preferred_start_date} />
                  <Pair label="Schedule" value={record.weekly_schedule || record.schedule} />
                  <Pair label="Frequency" value={record.frequency || record.coverage_type} />
                </div>
              </DetailSection>
              <DetailSection title="Facility Needs">
                <div className="grid gap-2.5 text-sm">
                  <Pair label="Special Requirements" value={record.special_requirements} />
                  <Pair label="Compliance Notes" value={record.compliance_notes} />
                  <Pair label="Decision Timeline" value={record.decision_timeline} />
                  <Pair label="Competing Vendors" value={record.competing_vendors} />
                </div>
              </DetailSection>
              {record.notes && (
                <DetailSection title="Request Notes">
                  <div className="text-sm whitespace-pre-wrap" style={{ color: "#334155" }}>{record.notes}</div>
                </DetailSection>
              )}
            </TabsContent>

            {/* SERVICES — flat list of what the facility requested */}
            <TabsContent value="services" className="m-0 p-5 space-y-3" data-testid="facility-tab-content-services">
              <DetailSection title="Services Requested">
                {(record.services_requested?.length || record.services?.length) ? (
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    {(record.services_requested || record.services || []).map((s, i) => (
                      <li key={i} className="rounded-xl border p-2.5 flex items-center gap-2"
                          style={{ borderColor: "#E2E8F0", background: "#F8FAFC" }}>
                        <span className="h-5 w-5 rounded grid place-items-center"
                              style={{ background: "rgba(15,109,93,0.14)", color: "#0F6D5D" }}>✓</span>
                        <span style={{ color: "#0B2545" }}>{s}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs" style={{ color: "#64748B" }}>No services specified on this request.</div>
                )}
              </DetailSection>
              <DetailSection title="Coverage">
                <div className="grid gap-2.5 text-sm">
                  <Pair label="Service Area" value={record.service_area} />
                  <Pair label="Coverage Type" value={record.coverage_type} />
                  <Pair label="Hours" value={record.service_hours} />
                  <Pair label="Urgency" value={record.urgency} />
                </div>
              </DetailSection>
            </TabsContent>

            {/* CONTRACT PATIENTS ROSTER */}
            <TabsContent value="patients" className="m-0 p-4 space-y-3" data-testid="facility-tab-content-patients">
              <FacilityPatientRoster facilityId={record.id} onOpen={(p) => onOpenContractPatient?.(p.id)} />
            </TabsContent>

            {/* STANDING ORDERS */}
            <TabsContent value="orders" className="m-0 p-4 space-y-3" data-testid="facility-tab-content-orders">
              <StandingOrdersPanel
                facilityId={record.id}
                facilityName={record.facility_name}
              />
            </TabsContent>

            {/* APPOINTMENTS */}
            <TabsContent value="appointments" className="m-0 p-4 space-y-3" data-testid="facility-tab-content-appointments">
              <FacilityAppointmentsTab facilityId={record.id} />
            </TabsContent>

            {/* MESSAGES */}
            <TabsContent value="messages" className="m-0 p-4 space-y-3" data-testid="facility-tab-content-messages">
              <FacilityMessagesTab facilityId={record.id} facility={record} />
            </TabsContent>

            {/* DOCUMENTS */}
            <TabsContent value="documents" className="m-0 p-4 space-y-3" data-testid="facility-tab-content-documents">
              <FacilityDocumentsTab facilityId={record.id} facility={record} />
            </TabsContent>

            {/* CONTRACTS / BILLING */}
            <TabsContent value="contract" className="m-0 p-4 space-y-4" data-testid="facility-tab-content-contract">
              <InfoCard title="Contract" onEdit={() => setEditing(true)}>
                <PairRow label="Contract Value" value={record.contract_value != null ? fmtMoney0(record.contract_value) : "—"} />
                <PairRow label="Monthly Revenue" value={record.monthly_revenue != null ? fmtMoney0(record.monthly_revenue) : "—"} />
                <PairRow label="Monthly Volume Estimate" value={record.monthly_volume_estimate ? `${record.monthly_volume_estimate} draws` : "—"} />
                <PairRow label="Contract Start" value={fmtDate(record.contract_start_date)} />
                <PairRow label="Contract End" value={fmtDate(record.contract_end_date)} />
                <PairRow label="Renewal Date" value={fmtDate(record.contract_renewal_date)} />
              </InfoCard>
            </TabsContent>

            {/* BILLING (new) */}
            <TabsContent value="quote-pricing" className="m-0 p-4 space-y-4" data-testid="facility-tab-content-quote-pricing">
              <InfoCard title="Billing" onEdit={() => setEditing(true)}>
                <PairRow label="Pricing Model" value={PRICING_MODELS.find((m) => m.value === record.pricing_model)?.label || record.pricing_model || "—"} />
                <PairRow label="Billing Frequency" value={BILLING_FREQUENCIES.find((m) => m.value === record.billing_frequency)?.label || record.billing_frequency || "—"} />
                <PairRow label="Payment Terms" value={PAYMENT_TERMS.find((m) => m.value === record.payment_terms)?.label || record.payment_terms || "—"} />
                <PairRow label="Contract Value" value={record.contract_value != null ? `${fmtMoney0(record.contract_value)} / month` : "—"} />
                <PairRow label="Outstanding Balance"
                  value={record.outstanding_balance != null ? fmtMoney0(record.outstanding_balance) : "$0.00"}
                  valueColor={record.outstanding_balance > 0 ? "#B91C1C" : "#065F46"}
                />
                <PairRow label="Last Payment" value={fmtDate(record.last_payment_date)} />
                <PairRow label="Next Invoice" value={fmtDate(record.next_invoice_date)} />
                <PairRow label="Collection Status" value={record.collection_status || "—"} />
              </InfoCard>
              <div
                className="rounded-xl border p-4 text-center text-xs"
                style={{ borderColor: "#E2E8F0", background: "#FFF", color: "#94A3B8" }}
                data-testid="billing-invoices-placeholder"
              >
                Invoice history & Stripe payment links surface here after a contract is signed.
                Generate invoices from the <b style={{ color: "#0B2545" }}>Next Action</b> card on the Overview tab.
              </div>
            </TabsContent>

            {/* PROPOSALS */}
            <TabsContent value="proposals" className="m-0 p-5 space-y-3" data-testid="facility-tab-content-proposals">
              <FacilityProposalsPanel facility={record} onFacilityUpdate={onUpdate} />
            </TabsContent>

            {/* ACTIVITY */}
            <TabsContent value="notes-activity" className="m-0 p-5 space-y-5" data-testid="facility-tab-content-notes-activity">
              {/* Email thread */}
              <DetailSection title="Email Thread" testId="facility-email-thread">
                <div className="flex justify-end mb-2">
                  <Button
                    onClick={() => setShowAiEmail(true)}
                    size="sm" className="h-8 text-xs"
                    style={{ background: "#0B2545", color: "#FFF" }}
                    data-testid="activity-new-message"
                  >
                    <Sparkles className="h-3.5 w-3.5 mr-1" style={{ color: "#D4AF37" }} />
                    New AI Message
                  </Button>
                </div>
                {threadLoading ? (
                  <div className="py-6 flex items-center justify-center gap-2 text-sm" style={{ color: "#94A3B8" }}>
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading thread…
                  </div>
                ) : emailThread.length === 0 ? (
                  <div className="py-6 text-center text-sm" style={{ color: "#94A3B8" }}>
                    No emails sent or received yet.
                  </div>
                ) : (
                  <ul className="space-y-2" data-testid="email-thread-list">
                    {emailThread.map((m) => {
                      const meta = EMAIL_STATUS_META[m.status] || EMAIL_STATUS_META.sent;
                      const inbound = m.direction === "inbound";
                      return (
                        <li key={m.id}
                          className="rounded-lg border p-3"
                          style={{
                            borderColor: inbound ? "rgba(20,184,166,0.35)" : "#E2E8F0",
                            background: inbound ? "rgba(20,184,166,0.04)" : "#FFFFFF",
                          }}
                          data-testid={`email-thread-${m.id}`}
                        >
                          <div className="flex items-start justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded"
                                style={{ background: inbound ? "rgba(20,184,166,0.14)" : "rgba(11,37,69,0.06)", color: inbound ? "#0F6D5D" : "#0B2545" }}>
                                {inbound ? "IN" : "OUT"}
                              </span>
                              <span className="text-sm font-semibold truncate" style={{ color: "#0B2545" }}>
                                {m.subject || "(no subject)"}
                              </span>
                            </div>
                            <span
                              className="inline-flex items-center rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider"
                              style={{ background: meta.bg, color: meta.color, borderColor: meta.border }}
                              data-testid={`email-status-badge-${m.status}`}
                            >
                              {meta.label}
                            </span>
                            {m.provider === "gmail" ? (
                              <span
                                className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider"
                                style={{ background: "rgba(212,175,55,0.16)", color: "#7A5E12" }}
                                data-testid={`email-provider-${m.id}`}
                                title="Sent via your connected Gmail"
                              >
                                Gmail
                              </span>
                            ) : m.provider ? (
                              <span
                                className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider"
                                style={{ background: "rgba(148,163,184,0.18)", color: "#475569" }}
                                data-testid={`email-provider-${m.id}`}
                                title="Sent via Resend"
                              >
                                Resend
                              </span>
                            ) : null}
                          </div>
                          <div className="mt-1 text-[11px]" style={{ color: "#64748B" }}>
                            {inbound ? `From ${m.from}` : `To ${m.to}`}
                            {" · "}
                            {fmtDateTime(m.created_at)}
                          </div>
                          {m.error_message ? (
                            <div className="mt-1.5 text-[11px]" style={{ color: "#991B1B" }}>
                              <span className="font-semibold">Error:</span> {m.error_message}
                            </div>
                          ) : null}
                          {(m.opened_at || m.clicked_at || m.delivered_at || m.replied_at) ? (
                            <div className="mt-1.5 flex flex-wrap gap-1.5 text-[10px]" style={{ color: "#94A3B8" }}>
                              {m.delivered_at ? <span>Delivered {fmtTime(m.delivered_at)}</span> : null}
                              {m.opened_at ? <span>· Opened {fmtTime(m.opened_at)}</span> : null}
                              {m.clicked_at ? <span>· Clicked {fmtTime(m.clicked_at)}</span> : null}
                              {m.replied_at ? <span>· Replied {fmtTime(m.replied_at)}</span> : null}
                            </div>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </DetailSection>

              {/* Activity log */}
              <DetailSection title="Activity Timeline">
                {Array.isArray(record.activity_log) && record.activity_log.length ? (
                  <ul className="space-y-2.5" data-testid="facility-activity-log">
                    {[...record.activity_log].reverse().slice(0, 50).map((e, i) => {
                      const emailStatus = (e.action || "").startsWith("email_") ? (e.action || "").slice(6) : null;
                      const meta = emailStatus ? EMAIL_STATUS_META[emailStatus] : null;
                      return (
                        <li key={i} className="rounded-lg border p-3" style={{ borderColor: "#F1F5F9" }}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="text-sm font-medium flex items-center gap-2 flex-wrap" style={{ color: "#0F172A" }}>
                              {e.summary}
                              {meta ? (
                                <span
                                  className="inline-flex items-center rounded-full border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider"
                                  style={{ background: meta.bg, color: meta.color, borderColor: meta.border }}
                                >
                                  {meta.label}
                                </span>
                              ) : null}
                            </div>
                            <div className="text-[10px] shrink-0" style={{ color: "#94A3B8" }}>{fmtDateTime(e.ts)}</div>
                          </div>
                          <div className="mt-1 text-[10px] uppercase tracking-wider" style={{ color: "#7A5E12" }}>
                            {e.action || "note"} · {e.actor || "system"}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div className="text-sm text-center py-8" style={{ color: "#94A3B8" }}>
                    No activity yet for this facility.
                  </div>
                )}
              </DetailSection>
            </TabsContent>

            {/* AI */}
            <TabsContent value="ai" className="m-0 p-5" data-testid="facility-tab-content-ai">
              <FacilityAIPanel facility={record} onFacilityUpdate={onUpdate} />
            </TabsContent>
          </div>
        </Tabs>

        {/* Quick Actions footer — sticky action bar at the bottom of the modal.
            Every button maps to an existing handler so nothing is a stub. */}
        <div className="shrink-0 border-t px-4 py-3 flex flex-wrap items-center gap-2 bg-white"
             style={{ borderColor: "#E2E8F0" }}
             data-testid="facility-quick-actions">
          {record.phone && (
            <a href={`tel:${record.phone}`}
               className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-xs font-medium"
               style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
               data-testid="qa-footer-call">
              <Phone className="h-3.5 w-3.5" style={{ color: "#0F6D5D" }} /> Call
            </a>
          )}
          <button type="button" onClick={() => setShowEmail(true)}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-xs font-medium"
                  style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
                  data-testid="qa-footer-email">
            <Mail className="h-3.5 w-3.5" style={{ color: "#1D4ED8" }} /> Email
          </button>
          <button type="button" onClick={() => setTab("quote-pricing")}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-xs font-medium"
                  style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
                  data-testid="qa-footer-create-quote">
            <FileText className="h-3.5 w-3.5" style={{ color: "#7A5E12" }} /> Create Quote
          </button>
          <button type="button" onClick={() => setTab("contract")}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-xs font-medium"
                  style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
                  data-testid="qa-footer-create-contract">
            <FileText className="h-3.5 w-3.5" style={{ color: "#9A3412" }} /> Create Contract
          </button>
          <button type="button" onClick={() => setShowFollowUp(true)}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-xs font-medium"
                  style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
                  data-testid="qa-footer-schedule-followup">
            <CalendarClock className="h-3.5 w-3.5" style={{ color: "#B45309" }} /> Schedule Follow-Up
          </button>
          <button type="button" onClick={() => setShowActivityModal(true)}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-xs font-medium"
                  style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
                  data-testid="qa-footer-add-note">
            <FileText className="h-3.5 w-3.5" style={{ color: "#475569" }} /> Add Note
          </button>
          <div className="flex-1" />
          <button type="button"
                  onClick={() => {
                    const label = labelFor(record.status);
                    if (window.confirm(`Move "${record.facility_name}" to the next pipeline stage?\n\nCurrently: ${label}`)) {
                      advance();
                    }
                  }}
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-xs font-semibold"
                  style={{ background: "#0B2545", color: "#FFD774" }}
                  data-testid="qa-footer-next-stage">
            Move to Next Stage <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Email composer modal */}
        <Dialog open={showEmail} onOpenChange={setShowEmail}>
          <DialogContent data-testid="facility-email-dialog">
            <DialogHeader>
              <DialogTitle>Send email to {record.facility_name}</DialogTitle>
              <DialogDescription>Use a template or write a custom message — sent via Resend, logged to activity.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {emailError ? (
                <div
                  className="rounded-lg border p-3 text-xs"
                  style={{ borderColor: "rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.06)", color: "#991B1B" }}
                  data-testid="email-error-banner"
                >
                  <div className="font-semibold mb-0.5 uppercase tracking-wider text-[10px]">Delivery failed</div>
                  <div className="leading-snug">{emailError}</div>
                </div>
              ) : null}
              <div>
                <Label>Template</Label>
                <Select value={emailAction} onValueChange={setEmailAction}>
                  <SelectTrigger className="h-10 mt-1" data-testid="email-template-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FACILITY_EMAIL_ACTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {["send_quote", "invoice"].includes(emailAction) ? (
                <div>
                  <Label>Amount (USD)</Label>
                  <Input type="number" value={emailAmount} onChange={(e) => setEmailAmount(e.target.value)} className="h-10 mt-1" data-testid="email-amount" />
                </div>
              ) : null}
              <div>
                <Label>Custom message (optional)</Label>
                <Textarea rows={4} value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)} className="mt-1" data-testid="email-message" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowEmail(false)} className="h-9">Cancel</Button>
                <Button onClick={sendEmail} disabled={emailSending} className="h-9" style={{ background: "#0B2545", color: "#FFF" }} data-testid="email-send-button">
                  {emailSending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Mail className="h-3.5 w-3.5 mr-1" />}
                  Send email
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Activity log modal */}
        <Dialog open={showActivityModal} onOpenChange={setShowActivityModal}>
          <DialogContent data-testid="facility-log-activity-dialog">
            <DialogHeader>
              <DialogTitle>Log activity</DialogTitle>
              <DialogDescription>Record a call, meeting, note, or interaction.</DialogDescription>
            </DialogHeader>
            <LogActivityForm onSubmit={sendActivity} onCancel={() => setShowActivityModal(false)} />
          </DialogContent>
        </Dialog>

        {/* Schedule follow-up modal */}
        <Dialog open={showFollowUp} onOpenChange={setShowFollowUp}>
          <DialogContent data-testid="facility-followup-dialog">
            <DialogHeader>
              <DialogTitle>Schedule follow-up</DialogTitle>
              <DialogDescription>Sets the Next Step + date on this facility.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Next step description</Label>
                <Input value={followUpStep} onChange={(e) => setFollowUpStep(e.target.value)} placeholder="e.g. Call to discuss pricing" className="h-10 mt-1" data-testid="followup-step" />
              </div>
              <div>
                <Label>Due date</Label>
                <Input type="date" value={followUpDate} onChange={(e) => setFollowUpDate(e.target.value)} className="h-10 mt-1" data-testid="followup-date" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowFollowUp(false)} className="h-9">Cancel</Button>
                <Button onClick={scheduleFollowUp} className="h-9" style={{ background: "#0B2545", color: "#FFF" }} data-testid="followup-save">
                  <CalendarClock className="h-3.5 w-3.5 mr-1" /> Schedule
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* AI Email Composer */}
        <AIEmailComposerDialog
          facility={record}
          open={showAiEmail}
          onClose={() => setShowAiEmail(false)}
          onSent={async () => {
            try {
              const fresh = await import("@/lib/api").then((m) => m.getFacilityLead(record.id));
              onUpdate(fresh);
            } catch { onUpdate(record); }
          }}
        />

        {/* Generate Invoice Dialog */}
        <GenerateInvoiceDialog
          facility={record}
          open={showGenInvoice}
          onClose={() => setShowGenInvoice(false)}
          onCreated={async () => {
            try {
              const fresh = await import("@/lib/api").then((m) => m.getFacilityLead(record.id));
              onUpdate(fresh);
            } catch { onUpdate(record); }
          }}
        />

        {/* Generate Contract Dialog */}
        <GenerateContractDialog
          facility={record}
          open={showGenContract}
          onClose={() => setShowGenContract(false)}
          onCreated={async () => {
            try {
              const fresh = await import("@/lib/api").then((m) => m.getFacilityLead(record.id));
              onUpdate(fresh);
            } catch { onUpdate(record); }
            setTab("contracts");
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// Detail subcomponents
// =============================================================================
const DetailSection = ({ title, children, noTitleUppercase, testId }) => (
  <section className="rounded-xl border bg-white" style={{ borderColor: "#E2E8F0" }} data-testid={testId}>
    <div className="px-4 py-2.5 border-b" style={{ borderColor: "#F1F5F9" }}>
      <div className={`text-[11px] font-semibold ${noTitleUppercase ? "" : "uppercase tracking-[0.18em]"}`} style={{ color: "#475569" }}>
        {title}
      </div>
    </div>
    <div className="px-4 py-3">{children}</div>
  </section>
);

const Pair = ({ label, value, link }) => (
  <div className="flex items-start justify-between gap-3 text-sm">
    <span className="shrink-0 text-[11px] uppercase tracking-wider" style={{ color: "#64748B" }}>{label}</span>
    {link && value ? (
      <a href={link} className="text-right underline truncate max-w-[60%]" style={{ color: "#0B2545" }}>{value}</a>
    ) : (
      <span className="text-right font-medium truncate max-w-[60%]" style={{ color: "#0B2545" }}>{value || "—"}</span>
    )}
  </div>
);

// -----------------------------------------------------------------------------
// New atoms (reference-style)
// -----------------------------------------------------------------------------
const InfoCard = ({ title, children, testId, onEdit, editing, onSave, onCancel, saving }) => (
  <section
    className="rounded-xl border bg-white overflow-hidden"
    style={{ borderColor: "#E2E8F0" }}
    data-testid={testId}
  >
    <div className="px-4 py-3 flex items-center justify-between border-b" style={{ borderColor: "#F1F5F9" }}>
      <div className="text-[11px] uppercase tracking-[0.18em] font-bold" style={{ color: "#0F172A" }}>
        {title}
      </div>
      {editing ? (
        <div className="flex gap-1.5">
          <button onClick={onCancel} className="text-[11px] font-semibold px-2 py-1 rounded" style={{ color: "#64748B" }}>Cancel</button>
          <button
            onClick={onSave} disabled={saving}
            className="text-[11px] font-semibold px-2.5 py-1 rounded"
            style={{ background: "#0B2545", color: "#FFF" }}
            data-testid="info-card-save"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      ) : onEdit ? (
        <button
          onClick={onEdit}
          className="text-[11px] font-semibold px-2 py-0.5 rounded hover:bg-[#F1F5F9]"
          style={{ color: "#2563EB" }}
          data-testid="info-card-edit"
        >
          Edit
        </button>
      ) : null}
    </div>
    <div className="px-4 py-3 space-y-2.5">{children}</div>
  </section>
);

const PairRow = ({ label, value, link, valueColor }) => (
  <div className="flex items-center justify-between gap-3 py-1 text-sm">
    <span className="shrink-0 text-[12px]" style={{ color: "#64748B" }}>{label}</span>
    {link ? (
      <a href={link} className="text-right font-semibold truncate max-w-[60%] hover:underline" style={{ color: valueColor || "#0F172A" }}>
        {value ?? "—"}
      </a>
    ) : (
      <span className="text-right font-semibold truncate max-w-[60%]" style={{ color: valueColor || "#0F172A" }}>
        {value ?? "—"}
      </span>
    )}
  </div>
);

const NextActionCard = ({ record, onSendEmail, onScheduleFollowUp, onAdvance,
                          onGenerateProposal, onGenerateInvoice, onGenerateContract, onCall }) => {
  const stage = canonicalFacilityStatus(record.status);
  const stageLabel = FACILITY_STATUSES.find((s) => s.value === stage)?.label || stage;
  const recommended = record.next_step || STAGE_RECOMMENDED_ACTION[stage] || "Review facility and pick a next step.";
  const idx = FACILITY_STAGE_INDEX(record.status);
  const nextStageLabel = idx >= 0 && idx < FACILITY_CORE_PIPELINE.length - 1
    ? FACILITY_CORE_PIPELINE[idx + 1].label : null;
  const dueOverdue = isOverdue(record.next_step_due || record.next_followup_date);

  return (
    <section
      className="rounded-xl border bg-white overflow-hidden"
      style={{ borderColor: "#E2E8F0" }}
      data-testid="next-action-card"
    >
      <div className="px-4 py-3 flex items-center justify-between border-b" style={{ borderColor: "#F1F5F9" }}>
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold" style={{ color: "#0F172A" }}>Next Action</div>
        <button
          onClick={onScheduleFollowUp}
          className="text-[11px] font-semibold px-2 py-0.5 rounded hover:bg-[#F1F5F9]"
          style={{ color: "#2563EB" }}
          data-testid="next-action-edit"
        >
          Edit
        </button>
      </div>
      <div className="px-4 py-3 space-y-3">
        <PairRow label="Current Stage" value={stageLabel} />
        <PairRow label="Assigned To" value={record.account_manager || "Beckett"} />
        <div className="pt-1">
          <div className="text-[12px]" style={{ color: "#64748B" }}>Recommended Action</div>
          <div className="mt-1 text-[13px]" style={{ color: "#0F172A" }} data-testid="next-action-recommendation">
            {recommended}
          </div>
        </div>
        <div>
          <div className="text-[12px]" style={{ color: "#64748B" }}>Due Date</div>
          <div
            className="mt-1 inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[12px] font-semibold"
            style={{
              borderColor: dueOverdue ? "rgba(220,38,38,0.4)" : "#E2E8F0",
              background: dueOverdue ? "rgba(220,38,38,0.06)" : "#F8FAFC",
              color: dueOverdue ? "#B91C1C" : "#0F172A",
            }}
            data-testid="next-action-due"
          >
            <CalendarClock className="h-3.5 w-3.5" />
            {fmtDate(record.next_step_due || record.next_followup_date) || "Not scheduled"}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 pt-2">
          <button
            onClick={onSendEmail}
            className="col-span-2 h-10 rounded-lg font-semibold text-sm flex items-center justify-center gap-2"
            style={{ background: "#2563EB", color: "#FFF" }}
            data-testid="next-action-send-email"
          >
            <Mail className="h-4 w-4" /> Send Email
          </button>
          <button
            onClick={onCall}
            className="h-9 rounded-lg border font-semibold text-xs flex items-center justify-center gap-1.5"
            style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
            data-testid="next-action-call"
          >
            <Phone className="h-3.5 w-3.5" /> Call Facility
          </button>
          <button
            onClick={onScheduleFollowUp}
            className="h-9 rounded-lg border font-semibold text-xs flex items-center justify-center gap-1.5"
            style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
            data-testid="next-action-schedule"
          >
            <CalendarClock className="h-3.5 w-3.5" /> Schedule Follow-Up
          </button>
          <button
            onClick={onGenerateProposal}
            className="h-9 rounded-lg border font-semibold text-xs flex items-center justify-center gap-1.5"
            style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
            data-testid="next-action-proposal"
          >
            <FileText className="h-3.5 w-3.5" /> Generate Proposal
          </button>
          <button
            onClick={onGenerateContract}
            className="h-9 rounded-lg border font-semibold text-xs flex items-center justify-center gap-1.5"
            style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
            data-testid="next-action-contract"
          >
            <CheckSquare className="h-3.5 w-3.5" /> Generate Contract
          </button>
          <button
            onClick={onGenerateInvoice}
            className="col-span-2 h-9 rounded-lg border font-semibold text-xs flex items-center justify-center gap-1.5"
            style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
            data-testid="next-action-invoice"
          >
            <DollarSign className="h-3.5 w-3.5" /> Generate Invoice
          </button>
          {nextStageLabel ? (
            <button
              onClick={() => {
                if (window.confirm(`Move this facility to "${nextStageLabel}"?`)) onAdvance();
              }}
              className="col-span-2 h-9 rounded-lg border font-semibold text-xs flex items-center justify-center gap-1.5"
              style={{ borderColor: "#0B2545", color: "#0B2545" }}
              data-testid="next-action-advance"
            >
              <ArrowRight className="h-3.5 w-3.5" /> Move to {nextStageLabel}
            </button>
          ) : null}
        </div>
      </div>
    </section>
  );
};

const AssistantPreviewCard = ({ record, onOpenFullAI, onFacilityUpdate }) => {
  const ai = record.ai_analysis || {};
  const prob = ai.probability_to_close ?? ai.close_probability;
  const risk = ai.risk_score;
  const suggestions = ai.suggested_next_steps || (ai.follow_up_suggestion ? [ai.follow_up_suggestion] : []);
  const summary = ai.insights?.[0] || ai.follow_up_suggestion || "Run the AI analysis to get pricing, contract length, close probability and next-step recommendations for this facility.";

  return (
    <section
      className="rounded-xl border bg-white overflow-hidden"
      style={{ borderColor: "#E2E8F0" }}
      data-testid="assistant-preview-card"
    >
      <div className="px-4 py-3 flex items-center justify-between border-b" style={{ borderColor: "#F1F5F9" }}>
        <div className="flex items-center gap-2">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold" style={{ color: "#0F172A" }}>Assistant</div>
          <span className="text-[9px] font-bold rounded-full px-1.5 py-0.5" style={{ background: "rgba(212,175,55,0.18)", color: "#7A5E12" }}>BETA</span>
        </div>
      </div>
      <div className="px-4 py-3 space-y-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] font-semibold" style={{ color: "#94A3B8" }}>AI Recommendation</div>
          <div className="mt-1 text-[13px] leading-relaxed" style={{ color: "#0F172A" }} data-testid="ai-recommendation-text">
            {summary}
          </div>
        </div>
        {prob != null ? (
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] font-semibold" style={{ color: "#94A3B8" }}>Probability to Close</div>
            <div className="mt-1 flex items-center gap-2">
              <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: "#F1F5F9" }}>
                <div className="h-full rounded-full" style={{ width: `${prob}%`, background: prob >= 60 ? "#10B981" : prob >= 30 ? "#D97706" : "#B91C1C" }} />
              </div>
              <span className="text-xs font-bold" style={{ color: prob >= 60 ? "#065F46" : prob >= 30 ? "#7A5E12" : "#991B1B" }}>
                {prob >= 60 ? "High" : prob >= 30 ? "Medium" : "Low"} ({prob}%)
              </span>
            </div>
          </div>
        ) : null}
        {risk != null ? (
          <PairRow label="Risk Score" value={`${risk} / 100`} valueColor={risk >= 60 ? "#B91C1C" : risk >= 30 ? "#7A5E12" : "#065F46"} />
        ) : null}
        <button
          onClick={onOpenFullAI}
          className="w-full h-9 rounded-lg border font-semibold text-xs flex items-center justify-center gap-1.5"
          style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
          data-testid="ask-ai-assistant"
        >
          <Sparkles className="h-3.5 w-3.5" style={{ color: "#D4AF37" }} /> Ask AI Assistant
        </button>
        {suggestions.length ? (
          <div className="pt-2 border-t" style={{ borderColor: "#F1F5F9" }}>
            <div className="text-[10px] uppercase tracking-[0.15em] font-semibold mb-2" style={{ color: "#94A3B8" }}>Suggested Next Steps</div>
            <ul className="space-y-1.5">
              {suggestions.slice(0, 4).map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-[12px]" style={{ color: "#0F172A" }}>
                  <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: "#10B981" }} />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </section>
  );
};

const RecentActivityMini = ({ record, onViewAll }) => {
  const items = [...(record.activity_log || [])].reverse().slice(0, 4);
  return (
    <section
      className="rounded-xl border bg-white overflow-hidden"
      style={{ borderColor: "#E2E8F0" }}
      data-testid="recent-activity-mini"
    >
      <div className="px-4 py-3 flex items-center justify-between border-b" style={{ borderColor: "#F1F5F9" }}>
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold" style={{ color: "#0F172A" }}>Recent Activity</div>
        <button
          onClick={onViewAll}
          className="text-[11px] font-semibold hover:underline"
          style={{ color: "#2563EB" }}
          data-testid="recent-activity-view-all"
        >
          View all
        </button>
      </div>
      <div className="px-4 py-3 space-y-2.5">
        {items.length === 0 ? (
          <div className="text-xs py-3 text-center" style={{ color: "#94A3B8" }}>No activity yet.</div>
        ) : items.map((e, i) => {
          const action = String(e.action || "").toLowerCase();
          const Icon =
            action.includes("email") ? Mail :
            action.includes("call")  ? Phone :
            action.includes("proposal") ? FileText :
            action.includes("payment") || action.includes("invoice") ? DollarSign :
            ActivityIcon;
          const iconColor =
            action.includes("email_delivered") || action.includes("payment") ? "#065F46" :
            action.includes("email_bounced") || action.includes("failed") ? "#991B1B" :
            "#2563EB";
          return (
            <div key={i} className="flex items-start gap-2.5">
              <div className="h-7 w-7 rounded-lg grid place-items-center shrink-0"
                style={{ background: `${iconColor}14`, color: iconColor }}>
                <Icon className="h-3.5 w-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[12px] truncate" style={{ color: "#0F172A" }}>{e.summary || e.action || "Activity"}</div>
                <div className="text-[11px]" style={{ color: "#94A3B8" }}>{fmtDateTime(e.ts)}</div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const QuickActionTile = ({ icon: Icon, label, onClick, menu, testId }) => {
  if (menu) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex flex-col items-center gap-1 rounded-xl border py-2.5 hover:bg-[#F8FAFC] transition"
            style={{ borderColor: "#E2E8F0" }} data-testid={testId}>
            <Icon className="h-4 w-4" style={{ color: "#0B2545" }} />
            <span className="text-[10px] font-semibold" style={{ color: "#0B2545" }}>{label}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">{menu}</DropdownMenuContent>
      </DropdownMenu>
    );
  }
  return (
    <button onClick={onClick} className="flex flex-col items-center gap-1 rounded-xl border py-2.5 hover:bg-[#F8FAFC] transition"
      style={{ borderColor: "#E2E8F0" }} data-testid={testId}>
      <Icon className="h-4 w-4" style={{ color: "#0B2545" }} />
      <span className="text-[10px] font-semibold" style={{ color: "#0B2545" }}>{label}</span>
    </button>
  );
};

const QuickActionRow = ({ icon: Icon, label, onClick, primary, testId }) => (
  <button
    type="button" onClick={onClick}
    className="w-full flex items-center justify-between rounded-lg border px-3 py-2.5 hover:bg-[#F8FAFC] transition"
    style={{
      borderColor: primary ? "#0B2545" : "#E2E8F0",
      background: primary ? "#0B2545" : "#FFFFFF",
      color: primary ? "#FFFFFF" : "#0B2545",
    }}
    data-testid={testId}
  >
    <span className="inline-flex items-center gap-2 text-sm font-semibold">
      <Icon className="h-4 w-4" /> {label}
    </span>
    <ArrowRight className="h-3.5 w-3.5 opacity-70" />
  </button>
);

const EditFields = ({ form, setForm }) => {
  if (!form) return null;
  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <EditField label="Facility name" value={form.facility_name} onChange={set("facility_name")} />
      <EditField label="Facility type" value={form.facility_type} onChange={set("facility_type")} placeholder="e.g. Assisted Living" />
      <EditField label="Primary contact" value={form.contact_person} onChange={set("contact_person")} />
      <EditField label="Contact role" value={form.title_role} onChange={set("title_role")} />
      <EditField label="Phone" value={form.phone} onChange={set("phone")} />
      <EditField label="Email" value={form.email} onChange={set("email")} />
      <div className="sm:col-span-2">
        <EditField label="Address" value={form.address} onChange={set("address")} />
      </div>
      <EditField label="Account manager" value={form.account_manager} onChange={set("account_manager")} />
      <EditField label="Monthly revenue (USD)" type="number" value={form.monthly_revenue} onChange={set("monthly_revenue")} />
      <EditField label="Contract value (USD)" type="number" value={form.contract_value} onChange={set("contract_value")} />
      <EditField label="Monthly volume est." type="number" value={form.monthly_volume_estimate} onChange={set("monthly_volume_estimate")} />
      <EditField label="Contract start" type="date" value={form.contract_start_date} onChange={set("contract_start_date")} />
      <EditField label="Contract end" type="date" value={form.contract_end_date} onChange={set("contract_end_date")} />
      <EditField label="Renewal date" type="date" value={form.contract_renewal_date} onChange={set("contract_renewal_date")} />
      <SelectField label="Billing frequency" value={form.billing_frequency} onChange={set("billing_frequency")} options={BILLING_FREQUENCIES} />
      <SelectField label="Payment terms" value={form.payment_terms} onChange={set("payment_terms")} options={PAYMENT_TERMS} />
      <SelectField label="Pricing model" value={form.pricing_model} onChange={set("pricing_model")} options={PRICING_MODELS} />
      <EditField label="A/P contact" value={form.accounts_payable_contact} onChange={set("accounts_payable_contact")} />
      <EditField label="Billing email" value={form.billing_email} onChange={set("billing_email")} />
      <EditField label="Next step" value={form.next_step} onChange={set("next_step")} />
      <EditField label="Next step due" type="date" value={form.next_step_due} onChange={set("next_step_due")} />
      <div className="sm:col-span-2">
        <EditField label="Tags (comma-separated)" value={form.tags} onChange={set("tags")} />
      </div>
      <div className="sm:col-span-2">
        <Label>Notes</Label>
        <Textarea rows={3} value={form.notes} onChange={(e) => set("notes")(e.target.value)} className="mt-1" />
      </div>
    </div>
  );
};

const EditField = ({ label, value, onChange, type = "text", placeholder }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-wider" style={{ color: "#475569" }}>{label}</Label>
    <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-9 mt-1 text-sm" />
  </div>
);

const SelectField = ({ label, value, onChange, options }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-wider" style={{ color: "#475569" }}>{label}</Label>
    <Select value={value || ""} onValueChange={(v) => onChange(v)}>
      <SelectTrigger className="h-9 mt-1"><SelectValue placeholder="Select…" /></SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

// Inline edit fields for the Billing Summary card. Shown only when the
// card is in editing mode. Decoupled from the Facility Information
// edit-flow so billing can be prepared while the facility is still in
// Negotiation or Proposal stages.
const BillingEditFields = ({ form, setForm }) => {
  if (!form) return null;
  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="billing-edit-fields">
      <SelectField
        label="Pricing Model"
        value={form.pricing_model}
        onChange={set("pricing_model")}
        options={PRICING_MODELS}
      />
      <SelectField
        label="Payment Terms"
        value={form.payment_terms}
        onChange={set("payment_terms")}
        options={PAYMENT_TERMS}
      />
      <SelectField
        label="Billing Frequency"
        value={form.billing_frequency}
        onChange={set("billing_frequency")}
        options={BILLING_FREQUENCIES}
      />
      <EditField
        label="Contract Value (USD / month)"
        type="number"
        value={form.contract_value}
        onChange={set("contract_value")}
        placeholder="0"
      />
      <EditField
        label="Outstanding Balance (USD)"
        type="number"
        value={form.outstanding_balance}
        onChange={set("outstanding_balance")}
        placeholder="0"
      />
      <EditField
        label="Last Payment Date"
        type="date"
        value={form.last_payment_date}
        onChange={set("last_payment_date")}
      />
      <EditField
        label="Next Invoice Date"
        type="date"
        value={form.next_invoice_date}
        onChange={set("next_invoice_date")}
      />
    </div>
  );
};

const LogActivityForm = ({ onSubmit, onCancel }) => {
  const [action, setAction] = useState("note");
  const [summary, setSummary] = useState("");
  return (
    <div className="space-y-3">
      <div>
        <Label>Type</Label>
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="h-10 mt-1" data-testid="log-activity-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="note">Note</SelectItem>
            <SelectItem value="call">Phone call</SelectItem>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="meeting">Meeting</SelectItem>
            <SelectItem value="quote">Quote sent</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Summary</Label>
        <Textarea rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} className="mt-1" data-testid="log-activity-summary" />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} className="h-9">Cancel</Button>
        <Button onClick={() => onSubmit(action, summary)} disabled={!summary.trim()} className="h-9" style={{ background: "#0B2545", color: "#FFF" }} data-testid="log-activity-save">
          Save activity
        </Button>
      </div>
    </div>
  );
};

// =============================================================================
// Bottom row cards
// =============================================================================
const RecentActivityCard = ({ items }) => (
  <div className="rounded-2xl border bg-white" style={{ borderColor: "#E2E8F0" }} data-testid="facility-recent-activity-card">
    <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "#F1F5F9" }}>
      <div className="text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#475569" }}>Recent Activity</div>
      <button className="text-[11px] font-semibold" style={{ color: "#0B2545" }}>View All</button>
    </div>
    {items.length === 0 ? (
      <div className="px-5 py-8 text-center text-sm" style={{ color: "#94A3B8" }}>No activity yet.</div>
    ) : (
      <ul className="divide-y" style={{ borderColor: "#F1F5F9" }}>
        {items.map((e, i) => (
          <li key={i} className="px-5 py-3 flex items-start gap-3">
            <span className="mt-1 h-2 w-2 rounded-full shrink-0" style={{ background: "#D4AF37" }} />
            <div className="min-w-0 flex-1">
              <div className="text-sm" style={{ color: "#0B2545" }}>{e.summary}</div>
              <div className="text-[10px]" style={{ color: "#94A3B8" }}>{e.facility_name} · {fmtDateTime(e.ts)}</div>
            </div>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const UpcomingTasksCard = ({ items, onOpen }) => (
  <div className="rounded-2xl border bg-white" style={{ borderColor: "#E2E8F0" }} data-testid="facility-upcoming-tasks-card">
    <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "#F1F5F9" }}>
      <div className="text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#475569" }}>Upcoming Tasks</div>
      <button className="text-[11px] font-semibold" style={{ color: "#0B2545" }}>View All</button>
    </div>
    {items.length === 0 ? (
      <div className="px-5 py-8 text-center text-sm" style={{ color: "#94A3B8" }}>No upcoming tasks.</div>
    ) : (
      <ul className="divide-y" style={{ borderColor: "#F1F5F9" }}>
        {items.map((t) => (
          <li key={t.id} className="px-5 py-3 flex items-start gap-3 hover:bg-[#F8FAFC] cursor-pointer transition"
            onClick={() => onOpen?.(t.id)}>
            <CheckSquare className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "#94A3B8" }} />
            <div className="min-w-0 flex-1">
              <div className="text-sm" style={{ color: "#0B2545" }}>{t.next_step}</div>
              <div className="text-[10px]" style={{ color: "#94A3B8" }}>{t.facility_name}</div>
            </div>
            <div className="text-[10px] shrink-0" style={{ color: "#475569" }}>{fmtDate(t.due)}</div>
          </li>
        ))}
      </ul>
    )}
  </div>
);

// =============================================================================
// Add facility dialog
// =============================================================================
const AddFacilityDialog = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState({
    facility_name: "", contact_person: "", title_role: "", email: "", phone: "",
    address: "", service_needed: "Mobile Phlebotomy", coverage_needed: "One-Time",
    volume_staffing_needs: "", preferred_start_date: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.facility_name || !form.contact_person || !form.email || !form.phone) {
      toast.error("Facility name, primary contact, email, and phone are required.");
      return;
    }
    try {
      setBusy(true);
      const rec = await createFacilityLead(form);
      toast.success("Facility added.");
      onCreated(rec);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Failed to add facility.");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="facility-add-dialog">
        <DialogHeader>
          <DialogTitle>Add Facility / Provider</DialogTitle>
          <DialogDescription>Create a new facility record. You can fill in CRM details after creation.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <EditField label="Facility name *" value={form.facility_name} onChange={set("facility_name")} />
            <EditField label="Primary contact *" value={form.contact_person} onChange={set("contact_person")} />
            <EditField label="Role / Title" value={form.title_role} onChange={set("title_role")} />
            <EditField label="Email *" value={form.email} onChange={set("email")} />
            <EditField label="Phone *" value={form.phone} onChange={set("phone")} />
            <SelectField
              label="Service needed *"
              value={form.service_needed}
              onChange={set("service_needed")}
              options={[
                { value: "Mobile Phlebotomy", label: "Mobile Phlebotomy" },
                { value: "Lab Logistics", label: "Lab Logistics" },
                { value: "Medical Courier", label: "Medical Courier" },
                { value: "STAT Service", label: "STAT Service" },
                { value: "Specimen Pick Up", label: "Specimen Pick Up" },
                { value: "Staffing Solutions", label: "Staffing Solutions" },
                { value: "Other", label: "Other" },
              ]}
            />
            <SelectField
              label="Coverage *"
              value={form.coverage_needed}
              onChange={set("coverage_needed")}
              options={[
                { value: "One-Time", label: "One-Time" },
                { value: "Recurring Daily", label: "Recurring Daily" },
                { value: "Recurring Weekly", label: "Recurring Weekly" },
                { value: "Long-Term Contract", label: "Long-Term Contract" },
                { value: "Other", label: "Other" },
              ]}
            />
            <div className="sm:col-span-2">
              <EditField label="Address" value={form.address} onChange={set("address")} />
            </div>
            <div className="sm:col-span-2">
              <Label>Volume / Staffing needs</Label>
              <Textarea rows={2} value={form.volume_staffing_needs} onChange={(e) => set("volume_staffing_needs")(e.target.value)} className="mt-1" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="h-10">Cancel</Button>
            <Button type="submit" disabled={busy} className="h-10" style={{ background: "#0B2545", color: "#FFF" }} data-testid="facility-add-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}
              Add facility
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};


// Compact patient roster embedded inside the facility profile. Clicking a row
// asks the parent panel to open the contract-patient profile sheet.
const FacilityPatientRoster = ({ facilityId, onOpen }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listFacilityContractPatients(facilityId)
      .then((r) => { if (!cancelled) setRows(r.patients || []); })
      .catch(() => { /* silent — global toast on network err */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [facilityId]);

  if (loading) return (
    <div className="grid place-items-center py-10">
      <Loader2 className="h-5 w-5 animate-spin" style={{ color: "#0B2545" }} />
    </div>
  );
  if (!rows.length) return (
    <div className="rounded-xl border-2 border-dashed p-6 text-center text-sm"
         style={{ borderColor: "#E2E8F0", color: "#64748B" }}
         data-testid="facility-patients-empty">
      No contract patients yet.
      <div className="mt-2 text-[11px]">Add patients from the Clients page — they'll appear here linked to this facility.</div>
    </div>
  );
  return (
    <div className="space-y-2" data-testid="facility-patients-list">
      <div className="text-[11px] uppercase tracking-wider font-bold" style={{ color: "#0B2545" }}>
        {rows.length} patient{rows.length === 1 ? "" : "s"} on file
      </div>
      {rows.map((p) => (
        <button key={p.id} type="button" onClick={() => onOpen?.(p)}
                className="w-full text-left rounded-xl border p-3 bg-white hover:bg-[#F8FAFC] transition"
                style={{ borderColor: "#E2E8F0" }}
                data-testid={`facility-patient-row-${p.id}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-semibold" style={{ color: "#0B2545" }}>{p.full_name}</div>
              <div className="text-[11px]" style={{ color: "#64748B" }}>
                {p.contract_number ? `Contract #${p.contract_number}` : "No contract on file"}
                {p.mrn ? ` · MRN ${p.mrn}` : ""}
                {(p.authorized_services || []).length ? ` · ${p.authorized_services.slice(0,3).join(", ")}` : ""}
              </div>
            </div>
            <span className="text-[9px] uppercase font-bold tracking-wider px-2 py-0.5 rounded"
                  style={{ background: "rgba(212,175,55,0.16)", color: "#7A5E12" }}>
              Contract Patient
            </span>
          </div>
        </button>
      ))}
    </div>
  );
};
